import React, { useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useInView,
  animate,
} from "motion/react";
import { Helmet } from "react-helmet-async";
import { getLaunchText, trackEvent, formatPrice } from "../utils";
import { recordProductView, recordProductAction, rankAndPrioritizeProducts } from "../utils/recommendationEngine";
import { ProductCardInfo } from "./ProductCardInfo";
import {
  Check,
  Droplet,
  Clock,
  ShieldCheck,
  Sparkles,
  Box,
  Award,
  Zap,
  Layers,
  Compass,
  Heart,
  Truck,
  MapPin,
  Package,
  PackageOpen,
} from "lucide-react";

const TrackedSectionEvent: React.FC<{ 
  eventName: string; 
  productName: string; 
  extraParams?: Record<string, any>;
}> = ({ eventName, productName, extraParams = {} }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: "some" });
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (isInView && !hasFiredRef.current) {
      hasFiredRef.current = true;
      trackEvent(eventName, { item_name: productName, ...extraParams });
    }
  }, [isInView, eventName, productName, extraParams]);

  return <div ref={ref} className="h-[1px] w-full pointer-events-none" aria-hidden="true" />;
};

const TimelineNodeRow: React.FC<{
  tab: "top" | "heart" | "base" | "skin";
  i: number;
  notes: any;
}> = ({ tab, i, notes }) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: nodeRef,
    offset: ["start center", "end start"],
  });

  const isInView = useInView(nodeRef, { once: true, amount: "some" });
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (isInView && !hasFiredRef.current) {
      hasFiredRef.current = true;
      trackEvent("pdp_timeline_note_viewed", { note_type: tab, item_name: notes[tab]?.title || "Unknown Note" });
    }
  }, [isInView, tab, notes]);

  const [isLit, setIsLit] = React.useState(false);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setIsLit(latest > 0);
  });

  return (
    <motion.div
      className={`relative flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-12 py-12 md:py-20 group ${
        i % 2 !== 0 ? "md:flex-row-reverse" : ""
      }`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4, margin: "-10% 0px -10% 0px" }}
    >
      {/* Section 1: Title & Time */}
      <div
        className={`relative z-10 md:w-1/2 flex w-full pl-16 pr-6 md:pl-0 md:pr-0 ${i % 2 !== 0 ? "md:justify-start text-left" : "md:justify-end text-left md:text-right"}`}
      >
        <div className="flex flex-col gap-3 max-w-sm w-full">
          {notes[tab].label && (
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#DFB37A] font-bold">
              {notes[tab].label}
            </p>
          )}
          <h3 className="text-3xl md:text-4xl font-semibold tracking-tight text-white capitalize">
            {notes[tab].title}
          </h3>
          <div>
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#DFB37A]/5 border border-[#DFB37A]/20 text-[9px] text-[#DFB37A] font-medium uppercase font-mono tracking-wider">
              {notes[tab].tag}
            </div>
          </div>
        </div>
      </div>

      {/* Node Masks for Line Tracking (prevents line drawing beyond first/last nodes). */}
      {i === 0 && (
        <div className="absolute left-[36px] md:left-1/2 -translate-x-1/2 md:-translate-x-1/2 top-0 bottom-[calc(100%-3.5rem)] md:bottom-1/2 w-[32px] bg-[#0a0a0b] z-[5]"></div>
      )}
      {i === 2 && (
        <div className="absolute left-[36px] md:left-1/2 -translate-x-1/2 md:-translate-x-1/2 top-14 md:top-1/2 bottom-0 w-[32px] bg-[#0a0a0b] z-[5]"></div>
      )}

      {/* Node */}
      <div
        ref={nodeRef}
        className={`absolute left-[36px] md:left-1/2 -translate-x-1/2 md:-translate-x-1/2 top-14 md:top-1/2 md:-translate-y-1/2 w-4 h-4 rounded-full border-[3px] ring-[12px] ring-[#0a0a0b] z-10 transition-all duration-700 group-hover:border-[#DFB37A] group-hover:bg-[#DFB37A] group-hover:shadow-[0_0_15px_rgba(223,179,122,0.8)] ${
          isLit
            ? "bg-[#DFB37A] border-[#DFB37A] shadow-[0_0_15px_rgba(223,179,122,0.8)]"
            : "bg-[#0a0a0b] border-neutral-700"
        }`}
      ></div>

      {/* Section 2: Description Content */}
      <div
        className={`relative z-10 md:w-1/2 flex w-full pl-16 pr-6 md:pl-0 md:pr-0 ${i % 2 !== 0 ? "md:justify-end text-left md:text-right" : "md:justify-start text-left"}`}
      >
        <div className="max-w-sm mt-0">
          <p className="text-[#FFF1D6] text-xl md:text-2xl font-serif italic font-light leading-relaxed">
            &ldquo;{notes[tab].desc}&rdquo;
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const getCarouselFeatures = (isForHer: boolean) => [
  {
    title: (
      <>
        Crafted with a high perfume concentration so your scent{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] to-[#FFF1D6]">
          lasts all day long.
        </span>
      </>
    ),
    image: isForHer
      ? "https://lh3.googleusercontent.com/d/1QFrSbGz5sQGueA3TDzQ-Z3-V8hNTWUYQ"
      : "https://lh3.googleusercontent.com/d/1k6Y65FpDZ-VlwQoImQoGFX9Yzy8ACUFl",
  },
  {
    title: (
      <>
        Carefully hand-poured in exceptionally small,{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] to-[#FFF1D6]">
          exclusive craft batches.
        </span>
      </>
    ),
    image:
      "https://lh3.googleusercontent.com/d/1pOaUwRnI6JukWoIFv7pSmVNI-6QhEh7V",
  },
  {
    title: (
      <>
        Created using sustainable and{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] to-[#FFF1D6]">
          premium natural ingredients
        </span>{" "}
        sourced globally.
      </>
    ),
    image:
      "https://lh3.googleusercontent.com/d/1C6g76ev_PhNAQt6AT_sYF8EWNuRFpO8z",
  },
];

export function Pdp({
  product,
  allProducts = [],
  categories = [],
  onBack,
  onWaitlist: propOnWaitlist,
  onBuyNow: propOnBuyNow,
  onNavigateToProduct,
}: {
  product: any;
  allProducts?: any[];
  categories?: any[];
  onBack: () => void;
  onWaitlist: () => void;
  onBuyNow: () => void;
  onNavigateToProduct?: (product: any) => void;
}) {
  const onWaitlist = () => {
    recordProductAction(product, 'waitlist');
    propOnWaitlist();
  };

  const onBuyNow = () => {
    recordProductAction(product, 'buy');
    propOnBuyNow();
  };

  const [reviews, setReviews] = React.useState<any[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = React.useState(true);
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewName, setReviewName] = React.useState("");
  const [reviewPhone, setReviewPhone] = React.useState("");
  const [reviewRating, setReviewRating] = React.useState(5);
  const [reviewDesc, setReviewDesc] = React.useState("");
  const [reviewImages, setReviewImages] = React.useState<string[]>([]);
  const [showReviewOtpInput, setShowReviewOtpInput] = React.useState(false);
  const [reviewOtp, setReviewOtp] = React.useState("");
  const [reviewConfirmationResult, setReviewConfirmationResult] =
    React.useState<any>(null);
  const [reviewError, setReviewError] = React.useState("");
  const [imageError, setImageError] = React.useState("");
  const [reviewSuccessMessage, setReviewSuccessMessage] = React.useState("");
  const [isSubmittingReview, setIsSubmittingReview] = React.useState(false);

  const [showTopReviewArrow, setShowTopReviewArrow] = React.useState(false);
  const [showBottomReviewArrow, setShowBottomReviewArrow] =
    React.useState(true);
  const reviewScrollContainerRef = React.useRef<HTMLDivElement>(null);

  const handleReviewScroll = () => {
    if (reviewScrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        reviewScrollContainerRef.current;
      setShowTopReviewArrow(scrollTop > 10);
      setShowBottomReviewArrow(scrollTop + clientHeight < scrollHeight - 10);
    }
  };

  React.useEffect(() => {
    if (product?.id) {
      fetch(`/api/reviews/${product.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            const sortedReviews = data.data.sort((a: any, b: any) => {
              if (b.rating !== a.rating) {
                return b.rating - a.rating;
              }
              return (
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
              );
            });
            setReviews(sortedReviews);
            setTimeout(() => handleReviewScroll(), 100);
          }
          setIsLoadingReviews(false);
        })
        .catch((err) => {
          console.error("Error fetching reviews:", err);
          setIsLoadingReviews(false);
        });
    }
  }, [product?.id]);

  React.useEffect(() => {
    if (product?.id) {
      recordProductView(product);
    }
  }, [product?.id, product]);

  const avgRating =
    reviews.length > 0
      ? (
          reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length
        ).toFixed(1)
      : null;

  const exploreMoreProducts = React.useMemo(() => {
    return rankAndPrioritizeProducts(product, allProducts, 24);
  }, [allProducts, product?.id, product]);

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 400;
          const MAX_HEIGHT = 400;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL("image/jpeg", 0.5));
        };
      };
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    if (reviewImages.length + files.length > 3) {
      setImageError("Maximum 3 images allowed.");
      return;
    }
    setImageError("");

    const compressedImages = await Promise.all(
      Array.from(files).map((file: any) => compressImage(file)),
    );
    setReviewImages((prev) => [...prev, ...compressedImages]);
  };

  const handleSendReviewOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setReviewError("");
    if (reviewPhone.replace(/\D/g, "").length < 10) {
      setReviewError("Please enter a valid phone number.");
      return;
    }
    if (!reviewName || !reviewDesc || reviewRating < 1 || reviewRating > 5) {
      setReviewError("Please fill all required fields.");
      return;
    }

    setIsSubmittingReview(true);
    try {
      const { auth } = await import("../firebase");
      const { RecaptchaVerifier, signInWithPhoneNumber } =
        await import("firebase/auth");

      if (!(window as any).recaptchaVerifier) {
        (window as any).recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          { size: "invisible" },
        );
        await (window as any).recaptchaVerifier.render();
      }

      let formattedPhone = reviewPhone.trim();
      if (!formattedPhone.startsWith("+")) {
        formattedPhone = `+91${formattedPhone}`;
      }

      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        (window as any).recaptchaVerifier,
      );
      setReviewConfirmationResult(confirmation);
      setShowReviewOtpInput(true);
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      if (err.code === "auth/too-many-requests") {
        setReviewError(
          "Security block: Too many attempts from this IP. Please wait 20-30 mins.",
        );
      } else if (err.code === "auth/network-request-failed") {
        setReviewError("Network error: Check your internet or ad-blocker.");
      } else if (err.code === "auth/invalid-phone-number") {
        setReviewError("Invalid phone number. Please check and try again.");
      } else {
        setReviewError(err.message || "Error sending OTP.");
      }
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleVerifyReviewOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewConfirmationResult) return;
    setReviewError("");
    setIsSubmittingReview(true);

    try {
      const trimmedOtp = reviewOtp.trim().replace(/\s/g, "");
      await reviewConfirmationResult.confirm(trimmedOtp);
      // Success, now submit review
      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: product.id,
          name: reviewName,
          phone: reviewPhone,
          rating: reviewRating,
          description: reviewDesc,
          images: reviewImages,
        }),
      });
      const data = await res.json();
      if (data.success) {
        trackEvent("submit_review", {
          product_id: product?.id,
          product_name: product?.name,
        });
        setReviewSuccessMessage(
          "Review has been submitted & will reflect on the website in the next 12 hrs.",
        );
      } else {
        setReviewError(data.message || "Failed to submit review.");
      }
    } catch (err: any) {
      console.error("Error verifying OTP or submitting review:", err);
      setReviewError("Invalid OTP or server error.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentImageIndex(0);
  }, [product]);

  const [activeDescModalContent, setActiveDescModalContent] = React.useState<{name: string, description: string} | null>(null);
  const [timeLeft, setTimeLeft] = React.useState<{days: number, hours: number, minutes: number, seconds: number} | null>(null);
  const [isDiscountActive, setIsDiscountActive] = React.useState(false);
  const [isBundleDropdownOpen, setIsBundleDropdownOpen] = React.useState(false);

  useEffect(() => {
    if (!product?.discountValidity || !product?.discountPrice) {
      setIsDiscountActive(false);
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const validityDate = new Date(product.discountValidity).getTime();
      const now = new Date().getTime();
      const difference = validityDate - now;

      if (difference > 0) {
        setIsDiscountActive(true);
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        setIsDiscountActive(false);
        setTimeLeft(null);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [product?.discountValidity, product?.discountPrice]);


  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [direction, setDirection] = React.useState(0);
  const [selectedBundleProductIndex, setSelectedBundleProductIndex] = React.useState(0);
  const [hasInteractedWithGallery, setHasInteractedWithGallery] =
    React.useState(false);

  const [canScrollCarousel, setCanScrollCarousel] = React.useState(false);
  const carouselTrackRef = React.useRef<HTMLDivElement>(null);
  const [activeKayokiModal, setActiveKayokiModal] = React.useState<any>(null);

  const checkCarouselScroll = () => {
    if (carouselTrackRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = carouselTrackRef.current;
      setCanScrollCarousel(
        scrollWidth > clientWidth &&
          Math.ceil(scrollLeft + clientWidth) < scrollWidth - 10,
      );
    }
  };

  React.useEffect(() => {
    checkCarouselScroll();
    window.addEventListener("resize", checkCarouselScroll);
    
    const timer = setTimeout(() => {
      checkCarouselScroll();
    }, 250);

    return () => {
      window.removeEventListener("resize", checkCarouselScroll);
      clearTimeout(timer);
    };
  }, [product]);

  const showBuyingKayoki = (product?.accessMode === "waitlist" || product?.inviteOnly) && product?.accessMode !== "none";

  const isInviteOnly = !!product?.inviteOnly;
  const buyingKayokiSteps = React.useMemo(() => {
    if (isInviteOnly) {
      return [
        {
          step: "STEP 1",
          title: "Request an Invite",
          img: "https://lh3.googleusercontent.com/d/1eOSewajuIhupahlUUFa7j7BoJJ8K3LTU",
          bg: "rgb(42, 42, 42)",
          desc: "Submit your details to request exclusive access. Our team carefully reviews each application to curate a unique community of passionate collectors for our limited-batch releases.",
        },
        {
          step: "STEP 2",
          title: "Receive Allocation",
          img: "https://lh3.googleusercontent.com/d/1Vk_niiPTfti8YO8UuANQlx0frDo2lK2x",
          bg: "rgb(74, 85, 104)",
          desc: "Upon approval, a private invitation containing your unique access code and dedicated ordering window will be sent directly to your inbox.",
        },
        {
          step: "STEP 3",
          title: "Complete Reservation",
          img: "https://lh3.googleusercontent.com/d/1RH_FlZ7NycIKWb3eOD9V2hojEMkPyLYA",
          bg: "rgb(26, 74, 58)",
          desc: "Unlock our private store using your invitation code. Select your preferred fragrance, provide your shipping details, and secure your order before the reservation window closes.",
        },
        {
          step: "STEP 4",
          title: "Experience Kayoki",
          img: "https://lh3.googleusercontent.com/d/1pxzs6SdO1nFk5gGbu_8wU_Gg_CiEscnW",
          bg: "rgb(245, 245, 245)",
          desc: "Your hand-crafted Kayoki perfume arrives in numbered art piece boxes. Immerse yourself in premium, concentrated fragrance oils engineered for depth and longevity.",
        },
      ];
    } else {
      return [
        {
          step: "STEP 1",
          title: "Join the Waitlist",
          img: "https://lh3.googleusercontent.com/d/1eOSewajuIhupahlUUFa7j7BoJJ8K3LTU",
          bg: "rgb(42, 42, 42)",
          desc: "When the pieces are ready, an exclusive invitation is sent directly to your inbox. It contains the exact time of the drop and your personal purchase access link. Be prepared, as our allocations sell out within minutes.",
        },
        {
          step: "STEP 2",
          title: "Receive Launch Invite",
          img: "https://lh3.googleusercontent.com/d/1Vk_niiPTfti8YO8UuANQlx0frDo2lK2x",
          bg: "rgb(74, 85, 104)",
          desc: "When the pieces are ready, an exclusive invitation is sent directly to your inbox. It contains the exact time of the drop and your personal purchase access link. Be prepared, as our allocations sell out within minutes.",
        },
        {
          step: "STEP 3",
          title: "Place Your Order",
          img: "https://lh3.googleusercontent.com/d/1RH_FlZ7NycIKWb3eOD9V2hojEMkPyLYA",
          bg: "rgb(26, 74, 58)",
          desc: "On launch day, the website unlocks for those with invites. Simply select your desired fragrance, enter your details, and secure your order before the limited batch runs out.",
        },
        {
          step: "STEP 4",
          title: "Experience Kayoki",
          img: "https://lh3.googleusercontent.com/d/1pxzs6SdO1nFk5gGbu_8wU_Gg_CiEscnW",
          bg: "rgb(245, 245, 245)",
          desc: "Each Kayoki fragrance arrives meticulously packaged. Experience the depth of premium oils and hand-crafted perfection that lingers long after you've applied it.",
        },
      ];
    }
  }, [isInviteOnly]);

  const heroImages =
    product?.images && product.images.length > 0
      ? product.images
      : [
          product?.image ||
            "https://lh3.googleusercontent.com/d/1C6g76ev_PhNAQt6AT_sYF8EWNuRFpO8z",
        ];

  const nextHeroImage = (e?: React.MouseEvent | Event) => {
    if (e?.stopPropagation) e.stopPropagation();
    setDirection(1);
    setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    setHasInteractedWithGallery(true);
    trackEvent("pdp_gallery_interaction", { action: "next_image", item_name: product?.name });
  };

  const prevHeroImage = (e?: React.MouseEvent | Event) => {
    if (e?.stopPropagation) e.stopPropagation();
    setDirection(-1);
    setCurrentImageIndex(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length,
    );
    setHasInteractedWithGallery(true);
    trackEvent("pdp_gallery_interaction", { action: "prev_image", item_name: product?.name });
  };

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    if ("touches" in e) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartX.current = e.clientX;
      touchStartY.current = e.clientY;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    let touchEndX, touchEndY;
    if ("changedTouches" in e) {
      touchEndX = e.changedTouches[0].clientX;
      touchEndY = e.changedTouches[0].clientY;
    } else {
      touchEndX = e.clientX;
      touchEndY = e.clientY;
    }
    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;

    if (diffX > 50) {
      nextHeroImage();
    } else if (diffX < -50) {
      prevHeroImage();
    } else if (Math.abs(diffX) < 10 && Math.abs(diffY) < 10) {
      // It's a tap/click, not a scroll
      if (!ctaDisabled) {
        ctaText === "Buy Now" ? onBuyNow() : onWaitlist();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
      setHasInteractedWithGallery(true);
    }, 3000);
    return () => clearInterval(interval);
  }, [heroImages.length, currentImageIndex]);

  const activeSlideRef = useRef(0);
  const isCarouselInView = useInView(carouselRef, { amount: "some" });
  const autoScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoScrolling = useRef(false);

  useEffect(() => {
    activeSlideRef.current = activeSlide;
  }, [activeSlide]);

  const startAutoScroll = () => {
    if (autoScrollTimeoutRef.current)
      clearTimeout(autoScrollTimeoutRef.current);
    autoScrollTimeoutRef.current = setTimeout(() => {
      if (carouselRef.current) {
        const slideElement = carouselRef.current.children[0] as HTMLElement;
        if (slideElement) {
          const currentGap = window.innerWidth < 768 ? 16 : 24;
          const slideWidth = slideElement.offsetWidth + currentGap;
          const nextSlide = (activeSlideRef.current + 1) % 3;
          isAutoScrolling.current = true;
          carouselRef.current.style.scrollSnapType = "none";
          animate(carouselRef.current.scrollLeft, slideWidth * nextSlide, {
            duration: 1.5,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => {
              if (carouselRef.current) carouselRef.current.scrollLeft = v;
            },
          }).then(() => {
            isAutoScrolling.current = false;
            if (carouselRef.current)
              carouselRef.current.style.scrollSnapType = "";
          });
        }
      }
    }, 7000);
  };

  useEffect(() => {
    if (isCarouselInView) {
      startAutoScroll();
    } else {
      if (autoScrollTimeoutRef.current)
        clearTimeout(autoScrollTimeoutRef.current);
    }
    return () => {
      if (autoScrollTimeoutRef.current)
        clearTimeout(autoScrollTimeoutRef.current);
    };
  }, [isCarouselInView, activeSlide]);

  const handleCarouselScroll = () => {
    if (carouselRef.current && carouselRef.current.children.length > 0) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const slideElement = carouselRef.current.children[0] as HTMLElement;
      const currentGap = window.innerWidth < 768 ? 16 : 24;
      const slideWidth = slideElement.offsetWidth + currentGap;
      const newIndex = Math.round(scrollLeft / slideWidth);
      if (newIndex !== activeSlide) setActiveSlide(newIndex);

      // Reset timer on manual scroll
      if (isCarouselInView && !isAutoScrolling.current) {
        startAutoScroll();
      }
    }
  };

  const timelineRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: timelineScrollProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  const timelineHeight = useTransform(
    timelineScrollProgress,
    [0, 1],
    ["0%", "100%"],
  );

  useEffect(() => {
    if (activeDescModalContent || showReviewModal) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [activeDescModalContent, showReviewModal]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get("action") === "review") {
        setShowReviewModal(true);
      }
    }
  }, []);

  const stock = product?.inventory || 0;
  const hasLaunch = !!product?.dropDate;

  let badgeText = "Waitlist Open";
  let badgeColor = "bg-[#FFF4E5] text-[#E65100]";
  let ctaText = "Join Waitlist";
  let ctaDisabled = false;
  let bannerTitle: React.ReactNode = "Waitlist Open";
  let bannerSubtext = "Join the Waitlist & Get Early Access on Launch Day.";

  const productUnit = product?.productType === "Bundle" ? "Set" : "Bottle";

  const displayCapacity = React.useMemo(() => {
    if (product?.productType === "Bundle" && product?.bundleProducts?.length > 0) {
      return product.bundleProducts.map((bp: any) => {
        const p = allProducts?.find((ap) => ap.id === bp.productId);
        if (p) {
           return Array(bp.quantity).fill(p.capacity || "50 ML").join(" + ");
        }
        return "50 ML";
      }).join(" + ");
    }
    return product?.capacity || "50 ML";
  }, [product, allProducts]);

  if (product?.inviteOnly) {
    badgeText = "Invite Only";
    badgeColor = "bg-[#FFF4E5] text-[#E65100]";
    ctaText = "Get Invite";
    ctaDisabled = false;
    bannerTitle = "Invite Only";
    bannerSubtext = "Request an invite to acquire this exclusive release.";
  } else if (product?.accessMode !== "waitlist" && stock > 0 && !hasLaunch) {
    badgeText = "Live";
    badgeColor = "bg-[#E8F5E9] text-[#2E7D32]";
    ctaText = "Buy Now";
    bannerTitle = (
      <>
        <span className="text-[#DFB37A]">{stock}</span> {productUnit}{stock > 1 ? 's' : ''} Left
      </>
    );
    bannerSubtext = "Order Now Before Stock Out";
  } else if (product?.accessMode === "waitlist" || (stock > 0 && hasLaunch)) {
    badgeText = "Waitlist Open";
    badgeColor = "bg-[#FFF4E5] text-[#E65100]";
    ctaText = "Join Waitlist";
    bannerTitle = "Waitlist Open";
    bannerSubtext = "Join the Waitlist & Get Early Access on Launch Day.";
  } else if (stock <= 0) {
    badgeText = "Out of Stock";
    badgeColor = "bg-[#FDECEC] text-[#E53935]";
    ctaText = "Buy Now";
    ctaDisabled = true;
    bannerTitle = "We're Sold Out";
    bannerSubtext =
      "This Limited Collection Fragrance Is Sold Out. Try Again for the Next Season Launch.";
  }

  const description =
    product?.description ||
    "A deep, grounding warmth from rare woods that stays close to the skin for hours, commanding the room with an aura that speaks in whispers, not shouts.";
  const shortDesc =
    description.length > 100
      ? description.substring(0, 100) + "..."
      : description;

  const handleShare = async () => {
    trackEvent("share", { method: "native_share", item_name: product?.name });
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.name || "Kayoki Product",
          text: `Check out ${product?.name}`,
          url: window.location.href,
        });
      } catch (err: any) {
        if (
          err.name !== "AbortError" &&
          !err.message?.includes("cancellation")
        ) {
          console.error("Error sharing", err);
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const productName = product?.name || "Kayoki Fragrance";
  const urlName = productName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
  const seoUrl =
    typeof window !== "undefined"
      ? window.location.origin + window.location.pathname
      : `https://kayoki.shop/${urlName}`;

  return (
    <>
      <Helmet>
        <title>{`Kayoki | ${productName}`}</title>
        <link rel="canonical" href={seoUrl} />
        <meta name="description" content={description} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={`${productName} | Kayoki`} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content={
            product?.image || "https://kayoki.shop/default-meta-image.jpg"
          }
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={seoUrl} />
        <meta name="twitter:title" content={`${productName} | Kayoki`} />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content={
            product?.image || "https://kayoki.shop/default-meta-image.jpg"
          }
        />

        {/* Product listing specific (Google Schema JSON-LD) */}
        <script type="application/ld+json">
          {(() => {
            const currentPrice = isDiscountActive && product?.discountPrice
              ? product.discountPrice
              : (product?.price || "45000");
            const numericPrice = currentPrice.toString().replace(/[^0-9.]/g, "");

            const productSchema: any = {
              "@context": "https://schema.org/",
              "@type": "Product",
              name: productName,
              image: product?.image ? [product.image] : [],
              description: description,
              sku: product?.id?.toString() || "",
              mpn: product?.id?.toString() || "",
              brand: {
                "@type": "Brand",
                name: "Kayoki",
              },
              offers: {
                "@type": "Offer",
                url: seoUrl,
                priceCurrency: "INR",
                price: numericPrice,
                availability:
                  stock > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                itemCondition: "https://schema.org/NewCondition",
                priceValidUntil: isDiscountActive && product?.discountValidity
                  ? new Date(product.discountValidity).toISOString().split('T')[0]
                  : "2027-12-31",
              },
            };

            if (reviews && reviews.length > 0) {
              productSchema.aggregateRating = {
                "@type": "AggregateRating",
                ratingValue: avgRating || "5.0",
                reviewCount: reviews.length.toString(),
                bestRating: "5",
                worstRating: "1",
              };

              productSchema.review = reviews.slice(0, 5).map((r: any) => ({
                "@type": "Review",
                author: {
                  "@type": "Person",
                  name: r.name || "Verified Buyer",
                },
                datePublished: r.createdAt
                  ? (() => {
                      try {
                        return new Date(r.createdAt).toISOString().split('T')[0];
                      } catch {
                        return "2026-06-28";
                      }
                    })()
                  : "2026-06-28",
                reviewBody: r.description || "",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: (r.rating || 5).toString(),
                  bestRating: "5",
                  worstRating: "1",
                },
              }));
            }

            return JSON.stringify(productSchema);
          })()}
        </script>

        {/* Breadcrumb List (Google Schema JSON-LD) */}
        <script type="application/ld+json">
          {(() => {
            const category = categories?.find((c: any) => 
              product?.categoryId === c.id || 
              (c.productIds && (c.productIds.includes(Number(product?.id)) || c.productIds.includes(String(product?.id))))
            );
            
            const itemListElement: any[] = [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://kayoki.shop/",
              },
            ];

            if (category && category.name) {
              itemListElement.push({
                "@type": "ListItem",
                position: 2,
                name: category.name,
                item: `https://kayoki.shop/#category-${category.id}`,
              });
              itemListElement.push({
                "@type": "ListItem",
                position: 3,
                name: productName,
                item: seoUrl,
              });
            } else {
              itemListElement.push({
                "@type": "ListItem",
                position: 2,
                name: productName,
                item: seoUrl,
              });
            }

            return JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement,
            });
          })()}
        </script>
      </Helmet>
      <div className="bg-black text-[#1A1A1A] w-full relative z-[60] mt-[64px] md:mt-[72px]">
        {/* Split Screen Layout */}
        <section className="flex flex-col lg:flex-row items-start relative w-full bg-white mx-auto min-h-[calc(100dvh-64px)] md:min-h-[calc(100dvh-72px)]">
          {/* Left Column (Image Gallery) */}
          <div className="w-full lg:w-[45%] sticky top-[64px] md:top-[72px] z-0 h-[calc(100dvh-64px)] md:h-[calc(100dvh-72px)] flex-shrink-0 bg-white lg:border-r border-[#E5E5E5]/50">
            <TrackedSectionEvent eventName="pdp_gallery_viewed" productName={product?.name || ""} />
            <div className="product-media-section w-full h-full relative pb-0">
              {!hasInteractedWithGallery && heroImages.length > 1 && (
                <div className="absolute inset-0 flex items-center justify-center z-[70] pointer-events-none">
                  <motion.div
                    initial={{ x: 40, opacity: 0 }}
                    animate={{ x: -40, opacity: [0, 1, 1, 0] }}
                    transition={{
                      repeat: Infinity,
                      duration: 2.5,
                      ease: "easeInOut",
                    }}
                    className="flex flex-col items-center justify-center drop-shadow-md"
                  >
                    <div className="relative flex items-center justify-center pointer-events-none w-[30px] h-[30px]">
                      <div 
                        className="absolute bg-white/50 backdrop-blur-[2px] rounded-full shadow-sm"
                        style={{ width: '15px', height: '15px', top: '8.33%', left: '45.8%', transform: 'translate(-50%, -50%)' }}
                      ></div>
                      <svg 
                        width="30" 
                        height="30" 
                        viewBox="0 0 24 24" 
                        fill="white" 
                        stroke="black" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="relative z-10 drop-shadow-md"
                      >
                        <path d="M9 13V4a2 2 0 0 1 4 0v6.5a1.7 1.7 0 0 1 3 .5a1.7 1.7 0 0 1 3 .5a1.7 1.7 0 0 1 2 1.5v4.5a5.5 5.5 0 0 1-5.5 5.5h-5A6 6 0 0 1 4.5 17v-2a2.5 2.5 0 0 1 4.5-2z" />
                      </svg>
                    </div>
                  </motion.div>
                </div>
              )}
              {/* Desktop Hero Image */}
              <div className="hidden md:block w-full h-full relative">
                <div className="w-full h-full bg-white flex items-center justify-center relative overflow-hidden">
                  <div
                    className="absolute inset-0 w-full h-full bg-[linear-gradient(137deg,#FFF_5.67%,#E8E8E8_100%)] cursor-pointer"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleTouchStart}
                    onMouseUp={handleTouchEnd}
                  >
                    <AnimatePresence initial={false} custom={direction}>
                      <motion.img
                        key={currentImageIndex}
                        custom={direction}
                        variants={{
                          enter: (dir: number) => ({
                            x: dir > 0 ? "100%" : "-100%",
                            opacity: 0.5,
                          }),
                          center: {
                            x: 0,
                            opacity: 1,
                          },
                          exit: (dir: number) => ({
                            x: dir < 0 ? "100%" : "-100%",
                            opacity: 0.5,
                          }),
                        }}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        alt={product?.name || "Product image"}
                        className="object-cover w-full h-full absolute inset-0"
                        fetchPriority={
                          currentImageIndex === 0 ? "high" : "auto"
                        }
                        loading={currentImageIndex === 0 ? "eager" : "lazy"}
                        src={heroImages[currentImageIndex] || undefined}
                      />
                    </AnimatePresence>
                  </div>
                </div>
                {reviews.length > 0 ? (
                  <button
                    onClick={() => {
                      trackEvent("click_image_rating", {
                        product_id: product?.id,
                        product_name: product?.name,
                      });
                      document
                        .getElementById("reviews-section")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="absolute top-4 right-4 z-10 flex items-center gap-1 bg-white/80 backdrop-blur-md px-3 py-1.5 rounded-full shadow-sm hover:bg-white transition-colors cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-black">
                      {avgRating}
                    </span>
                    <svg
                      className="w-4 h-4 text-[#DFB37A] fill-current animate-[pulse_2s_ease-in-out_infinite] drop-shadow-[0_0_8px_rgba(223,179,122,0.8)]"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-gray-500 font-normal ml-0.5 text-xs">
                      ({reviews.length})
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      document
                        .getElementById("reviews-section")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="absolute top-4 right-4 z-10 flex items-center gap-1 bg-white/80 backdrop-blur-md px-3 py-1.5 rounded-full shadow-sm hover:bg-white transition-colors cursor-pointer"
                  >
                    <div className="flex -ml-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className="w-3.5 h-3.5 text-gray-400/50 fill-current"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="text-xs font-medium text-gray-500 ml-0.5">
                      No Reviews
                    </span>
                  </button>
                )}
              </div>

              {/* Mobile Image */}
              <div className="md:hidden w-full h-full relative">
                <div
                  className="w-full h-full relative bg-[linear-gradient(137deg,#FFF_5.67%,#E8E8E8_100%)] cursor-pointer"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={handleTouchStart}
                  onMouseUp={handleTouchEnd}
                >
                  <AnimatePresence initial={false} custom={direction}>
                    <motion.img
                      key={currentImageIndex}
                      custom={direction}
                      variants={{
                        enter: (dir: number) => ({
                          x: dir > 0 ? "100%" : "-100%",
                          opacity: 0.5,
                        }),
                        center: {
                          x: 0,
                          opacity: 1,
                        },
                        exit: (dir: number) => ({
                          x: dir < 0 ? "100%" : "-100%",
                          opacity: 0.5,
                        }),
                      }}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.8, ease: "easeInOut" }}
                      alt={product?.name || "Product image"}
                      className="object-cover w-full h-full absolute inset-0 pointer-events-none"
                      fetchPriority={currentImageIndex === 0 ? "high" : "auto"}
                      loading={currentImageIndex === 0 ? "eager" : "lazy"}
                      src={heroImages[currentImageIndex] || undefined}
                    />
                  </AnimatePresence>
                </div>
                {reviews.length > 0 ? (
                  <button
                    onClick={() => {
                      trackEvent("click_image_rating", {
                        product_id: product?.id,
                        product_name: product?.name,
                      });
                      document
                        .getElementById("reviews-section")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="absolute top-20 right-4 z-10 flex items-center gap-1 bg-white/80 backdrop-blur-md px-3 py-1.5 rounded-full shadow-sm hover:bg-white transition-colors cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-black">
                      {avgRating}
                    </span>
                    <svg
                      className="w-4 h-4 text-[#DFB37A] fill-current animate-[pulse_2s_ease-in-out_infinite] drop-shadow-[0_0_8px_rgba(223,179,122,0.8)]"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-gray-500 font-normal ml-0.5 text-xs">
                      ({reviews.length})
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      document
                        .getElementById("reviews-section")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="absolute top-20 right-4 z-10 flex items-center gap-1 bg-white/80 backdrop-blur-md px-3 py-1.5 rounded-full shadow-sm hover:bg-white transition-colors cursor-pointer"
                  >
                    <div className="flex -ml-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className="w-3.5 h-3.5 text-gray-400/50 fill-current"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="text-xs font-medium text-gray-500 ml-0.5">
                      No Reviews
                    </span>
                  </button>
                )}
                <div className="absolute max-md:bottom-[130px] md:bottom-24 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10 opacity-70 animate-[bounce_2s_infinite]">
                  <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-black drop-shadow-sm bg-white/60 px-3 py-1 rounded-full backdrop-blur-md">
                    Scroll
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-black drop-shadow-sm mt-1"
                  >
                    <path d="M12 5v14M19 12l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (Details Panel) */}
          <div className="flex-shrink-0 w-full lg:w-[55%] max-lg:relative max-lg:z-10 bg-white rounded-t-3xl lg:rounded-none max-lg:-mt-8 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] lg:shadow-none pt-0 max-lg:pt-6 pb-0 max-lg:pb-16 lg:flex lg:flex-col lg:justify-center lg:py-10 lg:min-h-[calc(100dvh-72px)]">
            <div className="w-full sm:w-[90%] lg:w-[80%] xl:w-[70%] mx-auto px-4 md:px-0">
              <div className="flex justify-between items-center">
                <div className="flex flex-wrap items-center gap-1.5 md:gap-2 fade-up-on-load max-md:pb-1 md:max-w-none">
                  <span className="inline-flex items-center px-2 py-1 md:px-3 rounded-full bg-[#F5F5F5] text-[9.5px] md:text-[11px] font-sans font-medium text-[#444444] uppercase tracking-wide whitespace-nowrap shrink-0">
                    Eau de Parfum
                  </span>
                  <span className="inline-flex items-center px-2 py-1 md:px-3 rounded-full bg-[#F5F5F5] text-[9.5px] md:text-[11px] font-sans font-medium text-[#444444] uppercase tracking-wide whitespace-nowrap shrink-0">
                    {product?.gender
                      ? (product.gender === "Male" ? "Men" : product.gender === "Female" ? "Women" : "Unisex")
                      : ((product?.name || "").toLowerCase().includes("her") ? "Women" : "Men")}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-1 md:px-3 rounded-full text-[9.5px] md:text-[11px] font-sans font-medium uppercase tracking-wide whitespace-nowrap shrink-0 ${badgeColor}`}
                  >
                    {badgeText}
                  </span>
                </div>
                <div className="flex items-center gap-3 fade-up-on-load delay-1">
                  <button
                    className="flex items-center justify-center w-10 h-10 cursor-pointer"
                    aria-label="Share product"
                    onClick={handleShare}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="w-[20px] h-[20px] md:w-[18px] md:h-[18px]"
                    >
                      <path
                        d="M17.25 15.0002C16.6886 15.003 16.135 15.1318 15.63 15.3771C15.1251 15.6225 14.6817 15.9781 14.3325 16.4177L8.84998 12.9902C9.0498 12.3453 9.0498 11.655 8.84998 11.0102L14.3325 7.58265C14.8889 8.27169 15.6713 8.74153 16.541 8.90896C17.4107 9.07638 18.3116 8.93058 19.0841 8.49737C19.8566 8.06417 20.4509 7.37152 20.7616 6.54214C21.0723 5.71276 21.0794 4.80016 20.7817 3.96603C20.484 3.13189 19.9006 2.43005 19.135 1.98483C18.3694 1.5396 17.4709 1.37974 16.5986 1.53356C15.7264 1.68737 14.9368 2.14493 14.3697 2.82519C13.8025 3.50545 13.4944 4.3645 13.5 5.25015C13.5035 5.58554 13.554 5.91877 13.65 6.24015L8.16748 9.66765C7.68347 9.05834 7.02189 8.61469 6.27445 8.3982C5.52701 8.18171 4.73073 8.2031 3.99599 8.45941C3.26126 8.71572 2.62445 9.19426 2.17385 9.82868C1.72325 10.4631 1.48117 11.222 1.48117 12.0002C1.48117 12.7783 1.72325 13.5372 2.17385 14.1716C2.62445 14.806 3.26126 15.2846 3.99599 15.5409C4.73073 15.7972 5.52701 15.8186 6.27445 15.6021C7.02189 15.3856 7.68347 14.942 8.16748 14.3327L13.65 17.7602C13.554 18.0815 13.5035 18.4148 13.5 18.7502C13.5 19.4918 13.7199 20.2169 14.132 20.8335C14.544 21.4502 15.1297 21.9309 15.8149 22.2147C16.5001 22.4985 17.2541 22.5728 17.9816 22.4281C18.709 22.2834 19.3772 21.9262 19.9016 21.4018C20.4261 20.8774 20.7832 20.2092 20.9279 19.4817C21.0726 18.7543 20.9984 18.0003 20.7145 17.3151C20.4307 16.6299 19.9501 16.0442 19.3334 15.6321C18.7167 15.2201 17.9917 15.0002 17.25 15.0002ZM17.25 3.00015C17.695 3.00015 18.13 3.13211 18.5 3.37935C18.87 3.62658 19.1584 3.97798 19.3287 4.38912C19.499 4.80025 19.5436 5.25265 19.4567 5.68911C19.3699 6.12556 19.1556 6.52647 18.841 6.84114C18.5263 7.15581 18.1254 7.3701 17.6889 7.45692C17.2525 7.54374 16.8001 7.49918 16.3889 7.32888C15.9778 7.15858 15.6264 6.8702 15.3792 6.50019C15.1319 6.13018 15 5.69516 15 5.25015C15 4.65342 15.237 4.08112 15.659 3.65916C16.0809 3.23721 16.6532 3.00015 17.25 3.00015ZM5.24998 14.2502C4.80498 14.2502 4.36996 14.1182 3.99995 13.871C3.62994 13.6237 3.34155 13.2723 3.17126 12.8612C3.00096 12.4501 2.9564 11.9977 3.04322 11.5612C3.13004 11.1247 3.34433 10.7238 3.65899 10.4092C3.97366 10.0945 4.37457 9.8802 4.81103 9.79339C5.24749 9.70657 5.69989 9.75113 6.11102 9.92142C6.52216 10.0917 6.87356 10.3801 7.12079 10.7501C7.36802 11.1201 7.49998 11.5551 7.49998 12.0002C7.49998 12.5969 7.26293 13.1692 6.84098 13.5911C6.41902 14.0131 5.84672 14.2502 5.24998 14.2502ZM17.25 21.0002C16.805 21.0002 16.37 20.8682 16 20.621C15.6299 20.3737 15.3416 20.0223 15.1713 19.6112C15.001 19.2001 14.9564 18.7477 15.0432 18.3112C15.13 17.8747 15.3443 17.4738 15.659 17.1592C15.9737 16.8445 16.3746 16.6302 16.811 16.5434C17.2475 16.4566 17.6999 16.5011 18.111 16.6714C18.5222 16.8417 18.8736 17.1301 19.1208 17.5001C19.368 17.8701 19.5 18.3051 19.5 18.7502C19.5 19.3469 19.2629 19.9192 18.841 20.3411C18.419 20.7631 17.8467 21.0002 17.25 21.0002Z"
                        fill="#666666"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>

              <h1 className="text-[28px] md:text-[32px] font-sans font-semibold fade-up-on-load delay-1 text-[#1A1A1A] leading-[135%] mt-6 lg:mt-5">
                {product?.name?.replace(" - For her", "") || "Monsoon 26"}
              </h1>
              <p className="text-[14px] mt-1 lg:mt-1 font-sans fade-up-on-load delay-2 text-[#555] leading-[135%] tracking-[0.16px] mb-[6px] lg:mb-[6px]">
                {hasLaunch
                  ? getLaunchText(product?.dropDate)
                  : displayCapacity}
              </p>
              <div className="text-[14px] font-sans fade-up-on-load delay-3 text-[#555] leading-[150%] tracking-[0.16px] mb-6 mt-4 lg:mb-4 lg:mt-3 lg:text-[15px]">
                {product?.productType === "Bundle" && !product?.description && product?.bundleProducts ? (
                  <div className="flex flex-col gap-2">
                    <div className="w-12 h-[1px] bg-neutral-300 mt-4 mb-4"></div>
                    <div className="flex items-center gap-2 text-black font-medium mb-1">
                      <div className="relative w-5 h-5 flex items-center justify-center">
                        <motion.div
                          animate={{ 
                            opacity: [1, 1, 0, 0, 1],
                            scale: [1, 1, 0.8, 0.8, 1],
                            rotate: [0, 0, -10, -10, 0]
                          }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute"
                        >
                          <Package size={18} strokeWidth={1.5} />
                        </motion.div>
                        <motion.div
                          animate={{ 
                            opacity: [0, 0, 1, 1, 0],
                            scale: [0.5, 0.5, 1, 1, 0.5],
                            rotate: [10, 10, 0, 0, 10],
                            y: [5, 5, 0, 0, 5]
                          }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute text-[#DFB37A]"
                        >
                          <PackageOpen size={20} strokeWidth={1.5} />
                        </motion.div>
                      </div>
                      In the Box
                    </div>
                    {product.bundleProducts.map((bp: any, idx: number) => {
                      const bpItem = allProducts?.find((p) => p.id === bp.productId);
                      if (!bpItem) return null;
                      return (
                        <div key={idx} className="flex items-center flex-wrap gap-1">
                          <button
                            onClick={() => {
                              trackEvent("click_bundle_item_description", {
                                bundle_id: product?.id,
                                bundle_name: product?.name,
                                item_id: bpItem.id,
                                item_name: bpItem.name,
                                item_notes: bpItem.notes || "",
                                item_price: typeof bpItem.price === "string" ? parseFloat(bpItem.price.replace(/[^0-9.]/g, "")) : (bpItem.price || 0),
                                item_quantity: bp.quantity,
                                item_capacity: bpItem.capacity || "50 ML"
                              });
                              setActiveDescModalContent({
                                name: bpItem.name?.replace(" - For her", "") || bpItem.name,
                                description: bpItem.description || "A deep, grounding warmth from rare woods that stays close to the skin for hours, commanding the room with an aura that speaks in whispers, not shouts."
                              });
                            }}
                            className="underline underline-offset-4 cursor-pointer hover:opacity-80 transition-opacity text-neutral-500 font-medium text-left"
                          >
                            {bpItem.name}
                          </button>
                          <span className="text-neutral-500 whitespace-nowrap">
                            - {bp.quantity} x {bpItem.capacity || "50 ML"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    {shortDesc}
                    {description.length > 100 && (
                      <button
                        onClick={() => {
                          setActiveDescModalContent({
                            name: product?.name?.replace(" - For her", "") || "Monsoon 26",
                            description: description
                          });
                          trackEvent("pdp_description_read", { item_name: product?.name });
                        }}
                        className="text-[#1A1A1A] text-[14px] lg:text-[15px] font-sans font-light tracking-wide uppercase hover:opacity-80 transition-opacity cursor-pointer underline underline-offset-4 ml-2"
                      >
                        Read more
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="product-info-section">
                <TrackedSectionEvent eventName="pdp_product_details_viewed" productName={product?.name || ""} />
                <div className="product-form fade-up-on-load delay-6">
                  <div className="hidden md:block text-[16px] font-sans font-bold text-[#1A1A1A] leading-[135%] ">
                    <div>
                      <div className="mb-2 mt-4 lg:mt-3">
                        <div className="product-price flex items-baseline md:text-[20px] gap-2 lg:mt-1">
                          {isDiscountActive && product?.discountPrice ? (
                            <>
                              <span className="font-bold text-green-600">₹{parseInt(String(product.discountPrice).replace(/[^0-9]/g, ""), 10).toLocaleString('en-IN')}</span>
                              <span className="text-[14px] line-through text-neutral-400 font-normal">
                                {formatPrice(product?.price || 45000)}
                              </span>
                            </>
                          ) : (
                            <span>{formatPrice(product?.price || 45000)}</span>
                          )}
                          <span className="text-[12px] font-sans font-normal text-neutral-400 hidden md:inline ml-1">
                            (Inclusive of all taxes)
                          </span>
                        </div>
                        {isDiscountActive && timeLeft && (
                          <div className="flex items-center mt-1 text-red-500 text-[13px] font-medium tracking-wide">
                            Ends in {String(timeLeft.days * 24 + timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="relative flex items-stretch min-h-[120px] lg:min-h-[110px] rounded-2xl overflow-hidden bg-[#070908] group mx-auto mb-0 lg:mb-4 mt-4 lg:mt-4">
                    <div
                      className="absolute -top-[70px] -left-6 w-[120px] h-full z-[1] pointer-events-none"
                      style={{
                        background:
                          "radial-gradient(rgba(248, 154, 40, 0.21), transparent 70%)",
                      }}
                    ></div>
                    <div className="relative z-10 pl-4 py-4 md:py-6 flex-1 flex flex-col justify-center">
                      <p className="manifesta-gradient-text-shimmer text-[9px] font-medium tracking-[1px] uppercase mb-3 text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] to-[#E5CCAA]">
                        {product?.name || "MONSOON 26"}
                      </p>
                      <p className="text-white text-[18px] font-semibold mb-1">
                        {bannerTitle}
                      </p>
                      <p className="text-[#CCCCCC] text-[11px] font-normal w-full md:w-[160%] lg:w-full leading-snug">
                        {bannerSubtext}
                      </p>
                    </div>
                    <div className="relative w-[55%] min-h-[130px] shrink-0">
                      <img
                        alt="Summer Collection"
                        loading="lazy"
                        decoding="async"
                        src="https://lh3.googleusercontent.com/d/1bnGHXX15jn7fX1AoyT1XvTy_zt8wNYRq"
                        className="grayscale-[0.2]"
                        style={{
                          position: "absolute",
                          height: "100%",
                          width: "100%",
                          inset: "0px",
                          objectFit: "cover",
                          objectPosition: "center center",
                          color: "transparent",
                        }}
                      />
                      <div
                        className="absolute inset-y-0 left-0 w-10 h-full z-[1] pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to right, rgb(7, 9, 8), transparent)",
                        }}
                      ></div>
                    </div>
                  </div>

                  <div
                    className="flex justify-center mt-8 mb-4 lg:mb-4 lg:mt-6 w-full"
                    data-desktop-add-to-cart="true"
                  >
                    <button
                      onClick={ctaText === "Buy Now" ? onBuyNow : onWaitlist}
                      disabled={ctaDisabled}
                      className={`group text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 bg-transparent md:bg-[#1A1A1A] md:text-white md:border-none md:pb-0 md:h-14 md:flex md:items-center md:justify-center md:w-full md:rounded-2xl font-sans font-light tracking-wide text-[15px] hover:opacity-80 md:hover:opacity-100 md:hover:bg-[#0a0a0a] md:hover:shadow-[0_8px_30px_rgba(223,179,122,0.15)] md:hover:scale-[1.02] md:active:scale-[0.98] transition-all duration-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed uppercase ${ctaText === "Buy Now" && !ctaDisabled ? "md:animate-glass-shine" : ""}`}
                    >
                      <span className="md:relative md:z-10 transition-colors duration-500 md:group-hover:text-[#DFB37A]">{ctaText === "Buy Now" ? "Order Now" : ctaText === "Get Invite" ? "Get Invite" : "Join Waitlist"}</span>
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between mt-10 mb-2 md:mb-8 lg:mb-6 w-full mx-auto border-t md:border-t-0 border-neutral-200 pt-8 md:pt-4 px-2 max-w-[28rem]">
                    <div className="flex flex-col items-center justify-start text-center gap-3.5 flex-1">
                      <div className="relative flex items-center justify-center h-[26px]">
                        <motion.div
                          animate={{ x: [0, -4, 0], opacity: [0.2, 0.8, 0.2] }}
                          transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
                          className="absolute right-[calc(100%-4px)] top-1/2 -translate-y-1/2 flex flex-col gap-[4px] -z-10"
                        >
                          <div className="w-2.5 h-[1.5px] bg-[#9c825a] rounded-full"></div>
                          <div className="w-4 h-[1.5px] bg-[#9c825a] rounded-full -translate-x-1"></div>
                          <div className="w-2 h-[1.5px] bg-[#9c825a] rounded-full"></div>
                        </motion.div>
                        <motion.div
                          animate={{ x: [0, 2, 0], y: [0, -1, 0] }}
                          transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                          className="relative z-10 bg-white"
                        >
                          <Truck className="w-[26px] h-[26px] text-[#9c825a]" strokeWidth={1.25} fill="white" />
                        </motion.div>
                      </div>
                      <span className="text-[10px] md:text-[11px] font-sans text-[#555] tracking-[0.16px] leading-[150%] uppercase">Free<br/>Shipping</span>
                    </div>
                    <div className="w-px h-14 bg-neutral-200 mx-2"></div>
                    <div className="flex flex-col items-center justify-start text-center gap-3.5 flex-1">
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                      >
                        <MapPin className="w-[26px] h-[26px] text-[#9c825a]" strokeWidth={1.25} />
                      </motion.div>
                      <span className="text-[10px] md:text-[11px] font-sans text-[#555] tracking-[0.16px] leading-[150%] uppercase">Delivering<br/>PAN-India</span>
                    </div>
                    <div className="w-px h-14 bg-neutral-200 mx-2"></div>
                    <div className="flex flex-col items-center justify-start text-center gap-3.5 flex-1">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
                      >
                        <Clock className="w-[26px] h-[26px] text-[#9c825a]" strokeWidth={1.25} />
                      </motion.div>
                      <span className="text-[10px] md:text-[11px] font-sans text-[#555] tracking-[0.16px] leading-[150%] uppercase">Next-Day<br/>Dispatch</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* removed empty space div */}
            </div>
          </div>
        </section>
        {/* Render the enhanced descriptive section modules for all products */}
        {(() => {
          const isForHer = (product?.name || "")
            .toLowerCase()
            .includes("her");

            // Define fragrance notes structure
            const forHimNotes = {
              top: {
                title: "Top Notes",
                label: "Spray",
                desc: "Fresh Citrus, Bergamot, Bright Fruits",
                tag: "Bright, Effervescent & Instant",
              },
              heart: {
                title: "Heart Notes",
                label: "30 Min",
                desc: "Pineapple, Coconut, Summer Fruits",
                tag: "Warm, Rich & Tropical",
              },
              base: {
                title: "Base Notes",
                label: "4 Hrs",
                desc: "Warm Woods, Soft Musk, Amber",
                tag: "Grounding, Deep & Sophisticated",
              },
              skin: {
                title: "Skin Scent",
                label: "End of Day",
                desc: "A soft, lingering heat with rich amber undertones",
                tag: "Personal, Safe & Magnetic",
              },
            };

            const forHerNotes = {
              top: {
                title: "Top Notes",
                label: "Spray",
                desc: "Crisp Citrus, Fresh Fruits",
                tag: "Clean, Dewy & Uplifting",
              },
              heart: {
                title: "Heart Notes",
                label: "30 Min",
                desc: "Soft Florals, White Flowers",
                tag: "Elegant, Airy & Sensual",
              },
              base: {
                title: "Base Notes",
                label: "4 Hrs",
                desc: "Warm Musk, Gentle Woods, Skin Accord",
                tag: "Intimate, Pure & Soft",
              },
              skin: {
                title: "Skin Scent",
                label: "End of Day",
                desc: "A beautiful whisper of soft skin accord and gentle woods",
                tag: "Calm, Alluring & Memorable",
              },
            };

            const getNotes = (p: any, isHer: boolean) => {
              const fallbackNotes = isHer ? forHerNotes : forHimNotes;
              return {
                top: { ...fallbackNotes.top, desc: p?.topNotes || fallbackNotes.top.desc },
                heart: { ...fallbackNotes.heart, desc: p?.heartNotes || fallbackNotes.heart.desc },
                base: { ...fallbackNotes.base, desc: p?.baseNotes || fallbackNotes.base.desc },
                skin: { ...fallbackNotes.skin }
              };
            };
            
            const bundleItems = product?.productType === "Bundle" ? (product.bundleProducts || []).map((bp: any) => {
               const bProduct = allProducts?.find((ap) => ap.id === bp.productId);
               return bProduct;
            }).filter(Boolean) : [];
            
            const activeNotesProduct = product?.productType === "Bundle" && bundleItems.length > 0 
              ? bundleItems[selectedBundleProductIndex] 
              : product;
            const activeIsForHer = (activeNotesProduct?.name || "").toLowerCase().includes("her");
            const notes = getNotes(activeNotesProduct, activeIsForHer);

            return (
              <div className="w-full bg-[#000000] text-white overflow-hidden font-sans select-none antialiased">
                {/* Section 2: The Story Behind Monsoon 26 (Immersive Dark Hero) */}
                <TrackedSectionEvent eventName="pdp_story_viewed" productName={product?.name || ""} />
                <div className="relative py-36 md:py-52 px-6 text-center flex flex-col justify-center items-center overflow-hidden min-h-[95vh]">
                  <motion.div
                    initial={{ scale: 1.1, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="absolute inset-0 z-0"
                  >
                    <img
                      src="https://lh3.googleusercontent.com/d/1xAieKw9iKt5vwzBZAnjN-a6EoVNsA0BZ"
                      alt="Lifestyle Backdrop"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover opacity-40 filter brightness-[0.45] contrast-[1.15]"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black via-black/15 to-black"></div>
                    
                    {/* Continuous Glowing Light Effect */}
                    <motion.div
                      className="absolute top-[0%] right-[0%] md:right-1/4 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-500/20 rounded-full filter blur-[100px] pointer-events-none"
                      animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />

                    {/* Spotlight effect */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#DFB37A]/5 rounded-full filter blur-[120px] pointer-events-none"></div>
                  </motion.div>

                  <div className="relative z-10 max-w-6xl mx-auto space-y-10 md:space-y-14 px-4">
                    <motion.h2
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                      className="text-5xl md:text-8xl tracking-[-0.04em] font-medium leading-[1.02] text-white"
                    >
                      A fragrance that took <br />
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#FFF1D6] to-[#DFB37A] font-serif px-2 -mx-2">
                        years
                      </span>{" "}
                      to become a feeling.
                    </motion.h2>

                    <motion.div
                      initial={{ opacity: 0, width: "0px" }}
                      whileInView={{ opacity: 1, width: "4rem" }}
                      transition={{
                        duration: 0.8,
                        delay: 0.2,
                        ease: "easeOut",
                      }}
                      viewport={{ once: true }}
                      className="h-[1px] bg-gradient-to-r from-transparent via-[#DFB37A]/30 to-transparent mx-auto"
                    ></motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.8,
                        delay: 0.3,
                        ease: "easeOut",
                      }}
                      viewport={{ once: true, margin: "-10%" }}
                      className="max-w-3xl mx-auto text-base md:text-lg text-neutral-450 font-light leading-relaxed space-y-6 pt-4"
                    >
                      <p className="leading-relaxed">
                        At Kayoki, perfection cannot be rushed. It took over
                        1,400 hours of meticulous formulation, multiple unique
                        iterations, and rigorous testing to achieve the final
                        composition of {product?.productType === 'Bundle' && bundleItems.length > 0 ? bundleItems.map((item: any) => item.name?.replace(/ - For (Him|her|Her)/i, '')).join(' & ') : (product?.name?.replace(/ - For (Him|her|Her)/i, '') || "Monsoon 26")} before it was finally ready to
                        be bottled.
                      </p>
                    </motion.div>

                    {/* Apple-Style Glassmorphic Metric Counters */}
                    <div className="grid grid-cols-3 gap-6 md:gap-12 max-w-2xl mx-auto pt-14 border-t border-neutral-900 text-center relative z-20">
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                        viewport={{ once: true }}
                        className="space-y-2 group"
                      >
                        <p className="text-3xl md:text-6xl font-light text-[#DFB37A] tracking-tighter">
                          {product?.longevity !== undefined ? product.longevity : "10"}Hr
                        </p>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                          High Longevity
                        </p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.5,
                          ease: "easeOut",
                        }}
                        viewport={{ once: true }}
                        className="space-y-2 group"
                      >
                        <p className="text-3xl md:text-6xl font-light text-[#DFB37A] tracking-tighter">
                          {product?.fragranceOil !== undefined ? product.fragranceOil : "30"}%
                        </p>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                          Fragrance Oil
                        </p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.6,
                          ease: "easeOut",
                        }}
                        viewport={{ once: true }}
                        className="space-y-2 group"
                      >
                        <p className="text-3xl md:text-6xl font-light text-[#DFB37A] tracking-tighter">
                          {product?.limitedQty !== undefined ? product.limitedQty : stock}
                        </p>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                          Limited {productUnit}s
                        </p>
                      </motion.div>
                    </div>

                    {/* Section Context CTA */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: 0.7,
                        ease: "easeOut",
                      }}
                      viewport={{ once: true }}
                      className="pt-16 pb-8 md:pt-24 relative z-20 flex justify-center"
                    >
                      <button
                        onClick={ctaText === "Buy Now" ? onBuyNow : onWaitlist}
                        disabled={ctaDisabled}
                        className="text-[#DFB37A] border-b border-[#DFB37A] pb-1 font-sans font-light tracking-wide text-sm md:text-base hover:opacity-80 md:hover:scale-[1.02] md:active:scale-[0.98] transition-all duration-300 cursor-pointer bg-transparent disabled:opacity-50 disabled:cursor-not-allowed uppercase inline-block"
                      >
                        {ctaText === "Buy Now"
                          ? "Experience The Feeling"
                          : ctaText === "Get Invite"
                          ? "Get Invite"
                          : "Join Waitlist"}
                      </button>
                    </motion.div>
                  </div>
                </div>

                {/* Section 4: Fragrance Journey (Visual Timeline Engine) */}
                <TrackedSectionEvent eventName="pdp_fragrance_journey_viewed" productName={product?.name || ""} />
                <div className="bg-[#0a0a0b] py-16 md:py-24 px-6 border-t border-neutral-900 relative">
                  <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-6 space-y-4 max-w-2xl mx-auto">
                      <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                        className="text-4xl md:text-6xl font-semibold tracking-tighter text-white"
                      >
                        How it <span className="italic font-light text-[#DFB37A] font-serif">unfolds.</span>
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.8,
                          delay: 0.2,
                          ease: "easeOut",
                        }}
                        viewport={{ once: true }}
                        className="text-sm text-neutral-400 font-light max-w-lg mx-auto leading-relaxed"
                      >
                        Experience the scent's journey. Scroll to see how the
                        fragrance evolves on your skin over time, from the
                        initial spray to the lingering base notes.
                      </motion.p>
                    </div>

                    {bundleItems.length > 0 && (
                      <div className="relative mb-10 mt-6 z-30 flex justify-center">
                        <button
                          onClick={() => {
                            trackEvent(isBundleDropdownOpen ? "close_bundle_dropdown" : "open_bundle_dropdown", {
                              bundle_id: product?.id,
                              bundle_name: product?.name,
                              selected_item_index: selectedBundleProductIndex,
                              selected_item_id: bundleItems[selectedBundleProductIndex]?.id,
                              selected_item_name: bundleItems[selectedBundleProductIndex]?.name
                            });
                            setIsBundleDropdownOpen(!isBundleDropdownOpen);
                          }}
                          className="flex items-center gap-2 text-[#DFB37A] font-semibold text-base md:text-lg tracking-wide hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          {bundleItems[selectedBundleProductIndex]?.name}
                          <svg 
                            className={`w-4 h-4 transition-transform duration-300 ${isBundleDropdownOpen ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        <AnimatePresence>
                          {isBundleDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute top-full mt-2 w-max min-w-[200px] bg-black/90 backdrop-blur-md border border-[#DFB37A]/30 rounded-xl overflow-hidden shadow-xl"
                            >
                              {bundleItems.map((item: any, idx: number) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    trackEvent("select_bundle_product", {
                                      bundle_id: product?.id,
                                      bundle_name: product?.name,
                                      selected_item_id: item.id,
                                      selected_item_name: item.name,
                                      selected_item_index: idx
                                    });
                                    setSelectedBundleProductIndex(idx);
                                    setIsBundleDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-5 py-3 text-sm md:text-base transition-colors ${
                                    selectedBundleProductIndex === idx
                                      ? "bg-[#DFB37A]/20 text-[#DFB37A] font-medium"
                                      : "text-white/80 hover:bg-white/5 hover:text-white"
                                  }`}
                                >
                                  {item.name}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Interactive Alternating Timeline */}
                    <div
                      ref={timelineRef}
                      className="relative max-w-4xl mx-auto mt-4"
                    >
                      {/* The Timeline Track Container */}
                      <div className="absolute left-[36px] md:left-1/2 -translate-x-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-neutral-900/50 z-0 overflow-hidden rounded-full">
                        {/* The Timeline Golden Progress Shimmering */}
                        <motion.div
                          className="absolute top-0 left-0 w-full shadow-[0_0_12px_rgba(223,179,122,0.8)] overflow-hidden"
                          style={{ height: timelineHeight }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-b from-[#DFB37A]/0 via-[#DFB37A] to-[#FFF1D6]"></div>
                          <motion.div
                            className="absolute left-0 w-full h-[150px]"
                            style={{
                              background:
                                "linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.9), transparent)",
                            }}
                            animate={{
                              top: ["-150px", "100%"],
                            }}
                            transition={{
                              duration: 2.5,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                          />
                        </motion.div>
                      </div>

                      {(["top", "heart", "base"] as const).map((tab, i) => (
                        <TimelineNodeRow
                          key={tab}
                          tab={tab}
                          i={i}
                          notes={notes}
                        />
                      ))}
                    </div>

                    {/* Section CTA */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      viewport={{ once: true }}
                      className="mt-16 md:mt-24 pb-8 text-center"
                    >
                      <button
                        onClick={ctaText === "Buy Now" ? onBuyNow : onWaitlist}
                        disabled={ctaDisabled}
                        className="text-[#DFB37A] border-b border-[#DFB37A] pb-1 font-sans font-light tracking-wide text-sm md:text-base hover:opacity-80 md:hover:scale-[1.02] md:active:scale-[0.98] transition-all duration-300 cursor-pointer bg-transparent disabled:opacity-50 disabled:cursor-not-allowed uppercase inline-block"
                      >
                        {ctaText === "Buy Now"
                          ? "Wear The Journey"
                          : ctaText === "Get Invite"
                          ? "Get Invite"
                          : "Join Waitlist"}
                      </button>
                    </motion.div>
                  </div>
                </div>

                {/* Section: Feature Carousel */}
                <div className="bg-[#000000] text-white py-24 md:py-32 relative border-t border-neutral-900 overflow-hidden">
                  <div className="max-w-[700px] mx-auto w-full">
                    <div className="text-center mb-12 space-y-4 px-6 md:px-0">
                      <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                        className="text-4xl md:text-5xl font-semibold tracking-tighter"
                      >
                        Uncompromising{" "}
                        <span className="italic font-light text-[#DFB37A] font-serif">
                          craft.
                        </span>
                      </motion.h2>
                    </div>
                  </div>

                  <div
                    ref={carouselRef}
                    onScroll={handleCarouselScroll}
                    className="flex overflow-x-auto snap-x snap-mandatory gap-4 md:gap-6 pb-8 [&::-webkit-scrollbar]:hidden px-[8vw] md:px-12 lg:px-24"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  >
                    {getCarouselFeatures(isForHer).map((feature, idx) => (
                      <div
                        key={idx}
                        className="snap-center md:snap-start shrink-0 w-[84vw] md:w-[400px] lg:w-[450px] h-[560px] md:h-[600px] lg:h-[660px] flex flex-col items-center bg-[#0a0a0b] border border-neutral-900 overflow-hidden relative"
                        style={{ borderRadius: "2rem" }}
                      >
                        <div className="absolute inset-0 z-0">
                          <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-[#0a0a0b] via-[#0a0a0b]/80 to-transparent z-10 pointer-events-none"></div>
                          <img
                            src={feature.image || undefined}
                            alt="feature"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover filter contrast-125 brightness-75"
                            referrerPolicy="no-referrer"
                          />
                        </div>
                        <div className="px-10 py-14 pt-16 text-center relative z-20 w-full">
                          <motion.h3
                            initial={{ opacity: 0, x: 30 }}
                            animate={{
                              opacity: activeSlide === idx ? 1 : 0.5,
                              x:
                                activeSlide === idx
                                  ? 0
                                  : activeSlide < idx
                                    ? 30
                                    : -30,
                            }}
                            transition={{
                              duration: 1.6,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                            className="text-2xl md:text-3xl font-medium leading-[1.3] tracking-tight text-white max-w-sm mx-auto"
                          >
                            {feature.title}
                          </motion.h3>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Carousel Navigation */}
                  <div className="flex items-center justify-center gap-6 mt-4 relative z-20">
                    <div className="bg-[#1c1c1e] px-4 py-3 rounded-full flex items-center gap-3">
                      {getCarouselFeatures(isForHer).map((_, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            if (
                              carouselRef.current &&
                              carouselRef.current.children.length > 0
                            ) {
                              const slideElement = carouselRef.current
                                .children[0] as HTMLElement;
                              const currentGap =
                                window.innerWidth < 768 ? 16 : 24;
                              const slideWidth =
                                slideElement.offsetWidth + currentGap;
                              isAutoScrolling.current = true;
                              carouselRef.current.style.scrollSnapType = "none";
                              animate(
                                carouselRef.current.scrollLeft,
                                slideWidth * idx,
                                {
                                  duration: 1.5,
                                  ease: [0.16, 1, 0.3, 1],
                                  onUpdate: (v) => {
                                    if (carouselRef.current)
                                      carouselRef.current.scrollLeft = v;
                                  },
                                },
                              ).then(() => {
                                isAutoScrolling.current = false;
                                if (carouselRef.current)
                                  carouselRef.current.style.scrollSnapType = "";
                              });
                            }
                          }}
                          className={`transition-all duration-[2000ms] ease-[cubic-bezier(0.16,1,0.3,1)] rounded-full cursor-pointer 
                        ${activeSlide === idx ? "w-8 h-2 bg-white" : "w-2 h-2 bg-neutral-600 hover:bg-neutral-400"}`}
                        ></div>
                      ))}
                    </div>
                  </div>

                  {/* Section CTA */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="mt-16 md:mt-24 pb-8 text-center relative z-20"
                  >
                    <button
                      onClick={ctaText === "Buy Now" ? onBuyNow : onWaitlist}
                      disabled={ctaDisabled}
                      className="text-[#DFB37A] border-b border-[#DFB37A] pb-1 font-sans font-light tracking-wide text-sm md:text-base hover:opacity-80 md:hover:scale-[1.02] md:active:scale-[0.98] transition-all duration-300 cursor-pointer bg-transparent disabled:opacity-50 disabled:cursor-not-allowed uppercase inline-block"
                    >
                      {ctaText === "Buy Now"
                        ? "Own The Craft"
                        : ctaText === "Get Invite"
                        ? "Get Invite"
                        : "Join Waitlist"}
                    </button>
                  </motion.div>
                </div>

                {false && (
                  <div className="bg-[#0B0B0C] text-white py-28 md:py-36 px-6 text-center overflow-hidden relative border-t border-neutral-900 transform-gpu">
                    {/* Static Center Glow */}
                    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-40">
                      <div
                        className="absolute top-1/2 left-1/2 w-[180vw] h-[180vw] md:w-[900px] md:h-[900px] rounded-full -translate-x-1/2 -translate-y-1/2"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(223,179,122,0.15) 0%, rgba(223,179,122,0) 60%)",
                        }}
                      />
                    </div>

                    {/* Dynamic Animated Radial Glows */}
                    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-100 md:opacity-80">
                      {/* Glow 1 */}
                      <motion.div
                        className="absolute w-[120vw] h-[120vw] md:w-[600px] md:h-[600px] rounded-full -translate-x-1/2 -translate-y-1/2"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(223,179,122,0.3) 0%, rgba(223,179,122,0) 40%)",
                        }}
                        animate={{
                          top: ["20%", "40%", "80%", "30%"],
                          left: ["30%", "70%", "20%", "50%"],
                          opacity: [0.1, 0.5, 0.1, 0.1],
                          scale: [0.8, 1.2, 0.8, 1],
                        }}
                        transition={{
                          duration: 15,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                      {/* Glow 2 */}
                      <motion.div
                        className="absolute w-[120vw] h-[120vw] md:w-[600px] md:h-[600px] rounded-full -translate-x-1/2 -translate-y-1/2"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(223,179,122,0.3) 0%, rgba(223,179,122,0) 40%)",
                        }}
                        animate={{
                          top: ["80%", "20%", "60%", "40%"],
                          left: ["80%", "20%", "60%", "80%"],
                          opacity: [0.1, 0.1, 0.5, 0.1],
                          scale: [1, 0.8, 1.2, 1],
                        }}
                        transition={{
                          duration: 18,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                      {/* Glow 3 */}
                      <motion.div
                        className="absolute w-[120vw] h-[120vw] md:w-[600px] md:h-[600px] rounded-full -translate-x-1/2 -translate-y-1/2"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(223,179,122,0.3) 0%, rgba(223,179,122,0) 40%)",
                        }}
                        animate={{
                          top: ["50%", "70%", "30%", "80%"],
                          left: ["20%", "80%", "50%", "30%"],
                          opacity: [0.5, 0.1, 0.1, 0.5],
                          scale: [1.2, 0.8, 1, 1.2],
                        }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                    </div>

                    <div className="max-w-2xl mx-auto space-y-8 relative z-10 px-4">
                      <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                        className="text-4xl md:text-7xl font-semibold leading-[1.05] tracking-tighter"
                      >
                        The waitlist is <br />
                        <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">
                          already growing.
                        </span>
                      </motion.h2>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.8,
                          delay: 0.2,
                          ease: "easeOut",
                        }}
                        viewport={{ once: true }}
                        className="max-w-md mx-auto text-sm md:text-base text-neutral-400 font-light leading-relaxed space-y-4"
                      >
                        <p>
                          {product?.productType === 'Bundle' && bundleItems.length > 0 ? bundleItems.map((item: any) => item.name?.replace(/ - For (Him|her|Her)/i, '')).join(' & ') : (product?.name?.replace(/ - For (Him|her|Her)/i, '') || "Monsoon 26")} officially launches on{" "}
                          <strong className="text-white">June 20, 2026</strong>.
                          Kayoki releases historically sell out in mere minutes.
                        </p>
                        <p>
                          Join the waitlist to secure priority early access,
                          giving you the best chance to acquire a {productUnit.toLowerCase()} before
                          the public release.
                        </p>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "backOut",
                        }}
                        viewport={{ once: true }}
                        className="pt-6"
                      >
                        <button
                          onClick={onWaitlist}
                          disabled={ctaDisabled}
                          className={`px-10 py-4.5 rounded-full font-sans font-bold text-xs md:text-sm tracking-widest uppercase border transition-all duration-300 ${
                            ctaDisabled
                              ? "border-neutral-800 bg-neutral-900 text-neutral-600 cursor-not-allowed"
                              : "bg-white text-black border-transparent hover:bg-neutral-100 cursor-pointer hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                          }`}
                        >
                          Join Waitlist
                        </button>
                      </motion.div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        {/* Buying a Kayoki Section */}
        {showBuyingKayoki && (
          <section className="w-full bg-black text-neutral-50 py-12 md:py-24 relative overflow-hidden border-t border-neutral-900">
            <div className="max-w-[100rem] mx-auto px-6 md:px-12 w-full flex flex-col items-center justify-center text-center mb-8 md:mb-12 gap-6">
              <div className="flex flex-col items-center">
                <motion.h2 
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                  className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter mb-4 text-center text-white"
                >
                  {isInviteOnly ? (
                    <>
                      Requesting a <br className="md:hidden" />
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">Kayoki</span>
                    </>
                  ) : (
                    <>
                      Buying a <br className="md:hidden" />
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">Kayoki</span>
                    </>
                  )}
                </motion.h2>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                  className="text-sm font-light text-neutral-400 tracking-wide max-w-md text-center"
                >
                  {isInviteOnly 
                    ? "How to request and secure your limited allocation." 
                    : "How to secure your limited edition piece."
                  }
                </motion.p>
              </div>
            </div>

            <div
              className="carousel_kayoki_ carousel-wrapper relative"
              style={{ transitionDelay: "100ms" }}
            >
              <style
                dangerouslySetInnerHTML={{
                  __html: `
        .carousel_kayoki_ {
          --card-width: min(260px, 75vw);
          --carousel-gap: 12px;
          --offset-start: 16px;
          --offset-end: 16px;
        }
        @media (min-width: 768px) {
          .carousel_kayoki_ {
            --card-width: 280px;
            --carousel-gap: 16px;
            --offset-start: 96px;
            --offset-end: 96px;
          }
        }
        .carousel_kayoki_ .carousel-track {
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: var(--card-width);
          gap: var(--carousel-gap);
          overflow-x: auto;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
          padding-block: 8px;
          padding-inline-start: var(--offset-start);
          padding-inline-end: var(--offset-end);
          scroll-padding-inline-start: var(--offset-start);
          transform: translateZ(0);
          backface-visibility: hidden;
        }
        .carousel_kayoki_ .carousel-track::-webkit-scrollbar {
          display: none;
        }
        .carousel_kayoki_ .carousel-track > * {
          scroll-snap-align: start;
          scroll-snap-stop: always;
          transform: translateZ(0);
          backface-visibility: hidden;
          will-change: transform;
        }
      `,
                }}
              />
              <div className="relative group">
                <div
                  className="carousel-track"
                  ref={carouselTrackRef}
                  onScroll={checkCarouselScroll}
                >
                  {buyingKayokiSteps.map((item, index) => (
                    <div
                      key={index}
                      className="group/card relative h-full flex flex-col rounded-[16px] text-left cursor-pointer"
                      style={{
                        background: "rgb(19, 19, 19)",
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        if (index !== 0) {
                          setActiveKayokiModal(item);
                        } else {
                          onWaitlist();
                        }
                      }}
                    >
                      <div
                        className="relative mb-10 overflow-hidden flex-shrink-0"
                        style={{
                          backgroundColor: item.bg,
                          width: "100%",
                          height: "220px",
                        }}
                      >
                        <img
                          alt={item.title}
                          className="w-full object-cover transition-transform duration-500 group-hover/card:scale-110"
                          loading="lazy"
                          src={item.img || undefined}
                          style={{ height: "220px" }}
                        />
                      </div>
                      <div className="flex flex-col px-[20px] flex-grow pb-[34px] h-full items-center text-center">
                        <p className="text-white/70 text-[12px] uppercase tracking-wider pb-2 font-medium">
                          {item.step}
                        </p>
                        <h3 className="text-white text-[20px] md:text-xl font-semibold pb-4 leading-[120%]">
                          {item.title}
                        </h3>
                        <span className="font-medium text-[14px] flex items-center justify-center gap-1 mt-auto text-[#DFB37A] hover:opacity-80 transition-opacity">
                          {index === 0 ? (isInviteOnly ? "Request Invite" : "Join Waitlist") : "Read more"}
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5l7 7-7 7"
                            ></path>
                          </svg>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {canScrollCarousel && (
                  <button
                    className="carousel-nav-btn carousel-nav-btn--next hidden md:flex absolute z-[5] items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 cursor-pointer right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg hover:scale-110"
                    aria-label="Next"
                    onClick={(e) => {
                      if (carouselTrackRef.current) {
                        carouselTrackRef.current.scrollBy({
                          left: 300,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1A1A1A"
                      strokeWidth="2"
                    >
                      <path
                        d="M9 18l6-6-6-6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Reviews Section */}
        <div
          id="reviews-section"
          className="w-full bg-black text-neutral-50 py-24 md:py-32 relative overflow-hidden"
        >
          <TrackedSectionEvent eventName="pdp_reviews_viewed" productName={product?.name || ""} />
          <div className="max-w-[700px] mx-auto w-full px-6 md:px-0">
            <div className="text-center mb-16 space-y-8">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                className="text-4xl md:text-5xl font-semibold tracking-tighter"
              >
                Customer's{" "}
                <span className="italic font-light text-[#DFB37A] font-serif">
                  Voice
                </span>
              </motion.h2>

              {reviews.length > 0 && avgRating ? (
                <div className="flex flex-col items-center justify-center gap-2 pb-4">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl font-light text-[#DFB37A]">
                      {avgRating}
                    </span>
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center text-[#DFB37A] -ml-0.5">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const numericAvg = Number(avgRating);
                          const isFull = numericAvg >= star;
                          const isHalf =
                            numericAvg >= star - 0.5 && numericAvg < star;

                          return (
                            <div
                              key={star}
                              className="relative w-5 h-5 flex items-center justify-center"
                            >
                              {/* Background empty star */}
                              <svg
                                className="w-5 h-5 text-white/20 fill-current absolute"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>

                              {/* Foreground star */}
                              <div
                                style={{
                                  clipPath: isFull
                                    ? "inset(0 0% 0 0)"
                                    : isHalf
                                      ? "inset(0 50% 0 0)"
                                      : "inset(0 100% 0 0)",
                                }}
                                className="absolute inset-0 text-[#DFB37A]"
                              >
                                <svg
                                  className="w-5 h-5 fill-current"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <span className="text-sm text-neutral-400 font-sans tracking-wide">
                        {reviews.length} Review{reviews.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 pb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center text-[#DFB37A] -ml-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div
                            key={star}
                            className="relative w-5 h-5 flex items-center justify-center"
                          >
                            <svg
                              className="w-5 h-5 text-white/20 fill-current absolute"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </div>
                        ))}
                      </div>
                      <span className="text-sm text-neutral-400 font-sans tracking-wide">
                        No Reviews
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  trackEvent("click_write_review", {
                    product_id: product?.id,
                    product_name: product?.name,
                  });
                  setShowReviewModal(true);
                }}
                className="text-[#DFB37A] border-b border-[#DFB37A] pb-1 font-sans font-light tracking-wide text-sm md:text-base hover:opacity-80 md:hover:scale-[1.02] md:active:scale-[0.98] transition-all duration-300 cursor-pointer bg-transparent disabled:opacity-50 disabled:cursor-not-allowed uppercase inline-block"
              >
                Write a Review
              </button>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-6 md:px-12">
            {isLoadingReviews ? (
              <div className="animate-pulse flex space-x-4">
                <div className="flex-1 space-y-4 py-1">
                  <div className="h-4 bg-white/10 rounded w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-white/10 rounded"></div>
                    <div className="h-4 bg-white/10 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-neutral-500 italic text-center w-full">
                No reviews yet. Be the first to review!
              </div>
            ) : (
              <div className="relative">
                <div
                  ref={reviewScrollContainerRef}
                  onScroll={handleReviewScroll}
                  className={`space-y-8 ${reviews.length > 2 ? "max-h-[400px] overflow-y-auto pr-4 custom-scrollbar" : ""}`}
                >
                  {reviews.map((r, i) => (
                    <div
                      key={i}
                      className="border-b border-white/10 pb-8 last:border-b-0"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-lg">{r.name}</span>
                        <div className="flex items-center text-[#DFB37A]">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const isFull = r.rating >= star;
                            const isHalf =
                              r.rating >= star - 0.5 && r.rating < star;

                            return (
                              <div
                                key={star}
                                className="relative w-4 h-4 flex items-center justify-center"
                              >
                                {/* Background empty star */}
                                <svg
                                  className="w-4 h-4 text-white/20 fill-current absolute"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>

                                {/* Foreground star */}
                                <div
                                  style={{
                                    clipPath: isFull
                                      ? "inset(0 0% 0 0)"
                                      : isHalf
                                        ? "inset(0 50% 0 0)"
                                        : "inset(0 100% 0 0)",
                                  }}
                                  className="absolute inset-0 text-[#DFB37A]"
                                >
                                  <svg
                                    className="w-4 h-4 fill-current"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <p className="text-neutral-400 mb-4">{r.description}</p>
                      {r.images && r.images.length > 0 && (
                        <div className="flex gap-4 overflow-x-auto">
                          {r.images.map((img: string, idx: number) => (
                            <img
                              key={idx}
                              src={img || undefined}
                              alt="Review"
                              className="w-24 h-24 object-cover rounded-md flex-shrink-0"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {reviews.length > 2 && showTopReviewArrow && (
                  <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black to-transparent pointer-events-none flex justify-center items-start pt-2">
                    <button 
                      onClick={() => {
                        if (reviewScrollContainerRef.current) {
                          reviewScrollContainerRef.current.scrollBy({ top: -200, behavior: 'smooth' });
                        }
                      }}
                      className="pointer-events-auto focus:outline-none cursor-pointer"
                      aria-label="Scroll Up"
                    >
                      <svg
                        className="w-6 h-6 text-white/50 animate-bounce"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
                {reviews.length > 2 && showBottomReviewArrow && (
                  <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none flex justify-center items-end pb-2">
                    <button 
                      onClick={() => {
                        if (reviewScrollContainerRef.current) {
                          reviewScrollContainerRef.current.scrollBy({ top: 200, behavior: 'smooth' });
                        }
                      }}
                      className="pointer-events-auto focus:outline-none cursor-pointer"
                      aria-label="Scroll Down"
                    >
                      <svg
                        className="w-6 h-6 text-white/50 animate-bounce"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Explore More Section */}
        {exploreMoreProducts.length > 0 && (
          <section className="w-full bg-black text-neutral-50 py-12 md:py-24 relative overflow-hidden">
            <TrackedSectionEvent 
              eventName="pdp_explore_more_viewed" 
              productName={product?.name || ""} 
              extraParams={{
                current_product_id: product?.id,
                current_product_name: product?.name,
                recommendation_count: exploreMoreProducts.length,
                recommendation_product_ids: exploreMoreProducts.map((p) => p.id),
                recommendation_product_names: exploreMoreProducts.map((p) => p.name)
              }}
            />
            <div className="max-w-[100rem] mx-auto px-6 md:px-12">
              <div className="flex flex-col items-center text-center justify-center mb-8 md:mb-12 gap-6">
                <div className="flex flex-col items-center">
                  <div className="flex flex-col items-center gap-3 mb-4">
                    <motion.h2 
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                      className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter text-center"
                    >
                      Explore{' '}
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">More</span>
                    </motion.h2>
                  </div>
                </div>
              </div>

              <div className={exploreMoreProducts.length === 1
                ? "flex justify-center w-full pb-4 md:pb-0"
                : "flex overflow-x-auto scroll-smooth overscroll-x-contain snap-x snap-mandatory hide-scrollbar gap-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-x-6 md:gap-y-12 pb-4 md:pb-0"
              }>
                {exploreMoreProducts.map((p, index) => (
                  <div
                    key={p.id}
                    className={exploreMoreProducts.length === 1
                      ? "group w-[75vw] md:w-[350px] max-w-full cursor-pointer"
                      : "group min-w-[65vw] snap-start md:min-w-0 md:w-auto cursor-pointer"
                    }
                    style={{ transitionDelay: `${index * 100}ms` }}
                    onClick={() => {
                      trackEvent("click_explore_more_product", {
                        current_product_id: product?.id,
                        current_product_name: product?.name,
                        clicked_product_id: p.id,
                        clicked_product_name: p.name,
                        clicked_product_notes: p.notes,
                        clicked_product_price: typeof p.price === "string" ? parseFloat(p.price.replace(/[^0-9.]/g, "")) : (p.price || 0),
                        click_position: index + 1
                      });
                      onNavigateToProduct?.(p);
                    }}
                  >
                    <div className="relative aspect-[3/4] bg-neutral-900 rounded-xl overflow-hidden mb-6">
                      <img
                        loading={index < 2 ? "eager" : "lazy"}
                        fetchPriority={index < 2 ? "high" : "auto"}
                        src={p.image || undefined}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-700 flex items-center justify-center"></div>
                    </div>
                    <ProductCardInfo 
                      product={p} 
                      titleClass="text-sm font-semibold tracking-tight uppercase text-white whitespace-nowrap"
                      notesClass="text-xs text-neutral-500 font-light tracking-wide truncate"
                      showInventory={true}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Review Modal */}
        <AnimatePresence>
          {showReviewModal && (
            <div
              className="fixed inset-0 flex justify-center items-end md:items-center p-0 md:p-6 z-[200]"
              style={{ overscrollBehavior: "none" }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/40 backdrop-blur-md cursor-pointer block"
                onClick={() => {
                  setShowReviewModal(false);
                  setReviewSuccessMessage("");
                  setShowReviewOtpInput(false);
                }}
                style={{ touchAction: "none" }}
              ></motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 150 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 150 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-scroll-container="true"
                className="w-full md:max-w-[400px] rounded-t-2xl md:rounded-xl max-h-[90vh] bg-white shadow-2xl flex flex-col z-10 overflow-hidden mb-0"
              >
                <style
                  dangerouslySetInnerHTML={{
                    __html: `
              .waitlist-scroll::-webkit-scrollbar {
                width: 6px;
                background-color: #ffffff;
              }
              .waitlist-scroll::-webkit-scrollbar-track {
                background-color: #ffffff;
              }
              .waitlist-scroll::-webkit-scrollbar-thumb {
                background-color: #d1d5db;
                border-radius: 10px;
              }
              .waitlist-scroll:hover::-webkit-scrollbar-thumb,
              .waitlist-scroll:focus-within::-webkit-scrollbar-thumb {
                background-color: #9ca3af;
              }
            `,
                  }}
                />

                {/* Sticky Header Portion */}
                {!showReviewOtpInput && !reviewSuccessMessage ? (
                  <div className="p-5 pb-0 relative z-20 bg-white shrink-0 md:rounded-t-xl min-h-[48px]">
                    <button
                      onClick={() => {
                        setShowReviewModal(false);
                        setReviewSuccessMessage("");
                      }}
                      className="absolute top-4 right-5 w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center hover:bg-[#E0E0E0] transition-colors cursor-pointer"
                      aria-label="Close"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M1 1L13 13M1 13L13 1"
                          stroke="#1A1A1A"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    <div className="text-[22px] font-sans font-semibold text-[#1A1A1A] mb-5 pr-10 mt-2">
                      <span className="font-[600]">Write a Review</span>
                    </div>
                  </div>
                ) : reviewSuccessMessage ? (
                  <div className="p-5 pb-0 relative z-20 bg-white md:rounded-t-xl shrink-0 min-h-[48px]">
                    <button
                      onClick={() => {
                        setShowReviewModal(false);
                        setReviewSuccessMessage("");
                      }}
                      className="absolute top-4 right-5 w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center hover:bg-[#E0E0E0] transition-colors cursor-pointer"
                      aria-label="Close"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M1 1L13 13M1 13L13 1"
                          stroke="#1A1A1A"
                          strokeWidth="2"
                          strokeLinecap="round"
                        ></path>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="p-5 pb-0 relative z-20 bg-white shrink-0 md:rounded-t-xl min-h-[48px]">
                    <button
                      onClick={() => {
                        setShowReviewModal(false);
                        setReviewSuccessMessage("");
                        setShowReviewOtpInput(false);
                      }}
                      className="absolute top-4 right-5 w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center hover:bg-[#E0E0E0] transition-colors cursor-pointer"
                      aria-label="Close"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M1 1L13 13M1 13L13 1"
                          stroke="#1A1A1A"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    <div className="text-[22px] font-sans font-semibold text-[#1A1A1A] mb-5 pr-10 mt-2">
                      <span className="font-[600]">Verify your phone</span>
                    </div>
                  </div>
                )}

                {/* Scrollable Form Portion */}
                <div
                  className={`overflow-y-auto waitlist-scroll flex-1 relative flex flex-col ${reviewSuccessMessage ? "px-6 pt-2 pb-10" : showReviewOtpInput ? "p-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]" : "p-5 pt-0 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]"}`}
                >
                  {reviewSuccessMessage ? (
                    <div className="flex flex-col items-center justify-center text-center flex-1 animate-in fade-in zoom-in-95 duration-300">
                      <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h3 className="text-[22px] font-sans font-semibold text-[#1A1A1A] mb-2">
                        Review Submitted
                      </h3>
                      <p className="text-[14px] text-gray-500 font-sans mb-6">
                        {reviewSuccessMessage}
                      </p>
                    </div>
                  ) : showReviewOtpInput ? (
                    <div className="flex flex-col animate-in fade-in zoom-in-95 duration-300">
                      <div className="relative mb-6">
                        <div className="flex items-center gap-3 p-3 bg-[#060606] rounded-[8px]">
                          <div className="relative w-[60px] h-[60px] shrink-0 overflow-hidden bg-white">
                            <img
                              alt={product?.name}
                              loading="lazy"
                              className="object-cover scale-[1.6]"
                              src={product?.image || undefined}
                              style={{
                                position: "absolute",
                                height: "100%",
                                width: "100%",
                                inset: 0,
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-[16px] text-[#FFFFFF]">
                              <span className="font-[600]">
                                {product?.name}
                              </span>
                            </h4>
                            <p className="text-[12px] font-[300] text-[#CCCCCC] mt-0.5">
                              {product?.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-[14px] text-gray-500 font-sans mb-6">
                        We sent a 6-digit code to +91{reviewPhone}
                      </p>
                      <form
                        className="space-y-4"
                        onSubmit={handleVerifyReviewOtp}
                      >
                        <div>
                          <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                            OTP <span className="text-[#E91E63]">*</span>
                          </label>
                          <input
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="Enter 6-digit OTP"
                            className="w-full bg-transparent text-[#1A1A1A] placeholder:text-gray-400 text-[16px] font-sans focus:outline-none border-b-[2px] pb-3 border-[#E4E4E7]"
                            type="text"
                            required
                            value={reviewOtp}
                            onChange={(e) =>
                              setReviewOtp(
                                e.target.value.replace(/\D/g, "").slice(0, 6),
                              )
                            }
                          />
                        </div>
                        {reviewError && (
                          <div className="bg-red-50 text-[#E91E63] text-[13px] p-2 rounded-md mb-2 text-center border border-red-100 animate-in fade-in">
                            {reviewError}
                          </div>
                        )}
                        <button
                          type="submit"
                          disabled={isSubmittingReview || reviewOtp.length < 6}
                          className="w-full h-[50px] bg-[#1A1A1A] text-white font-sans font-semibold text-[15px] hover:bg-[#333] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {isSubmittingReview
                            ? "Verifying..."
                            : "Verify & Submit"}
                        </button>
                      </form>
                    </div>
                  ) : (
                    <div className="flex flex-col animate-in fade-in zoom-in-95 duration-300">
                      <div className="relative mb-6">
                        <div className="flex items-center gap-3 p-3 bg-[#060606] rounded-[8px]">
                          <div className="relative w-[60px] h-[60px] shrink-0 overflow-hidden bg-white">
                            <img
                              alt={product?.name}
                              loading="lazy"
                              className="object-cover scale-[1.6]"
                              src={product?.image || undefined}
                              style={{
                                position: "absolute",
                                height: "100%",
                                width: "100%",
                                inset: 0,
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-[16px] text-[#FFFFFF]">
                              <span className="font-[600]">
                                {product?.name}
                              </span>
                            </h4>
                            <p className="text-[12px] font-[300] text-[#CCCCCC] mt-0.5">
                              {product?.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                      {reviewError && (
                        <div className="bg-red-50 text-[#E91E63] text-[13px] p-2 rounded-md mb-4 text-center border border-red-100 animate-in fade-in">
                          {reviewError}
                        </div>
                      )}
                      <form
                        className="space-y-5"
                        onSubmit={handleSendReviewOtp}
                      >
                        <div>
                          <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                            Full name <span className="text-[#E91E63]">*</span>
                          </label>
                          <input
                            value={reviewName}
                            onChange={(e) => setReviewName(e.target.value)}
                            placeholder="Enter full name"
                            aria-label="Full name"
                            className="w-full bg-transparent text-[#1A1A1A] placeholder:text-gray-400 text-[16px] font-sans focus:outline-none border-b-[2px] pb-3 border-[#E4E4E7]"
                            type="text"
                            required
                          />
                        </div>
                        <div className="sticky bottom-0 z-10 bg-white pt-2 pb-2">
                          <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                            Phone <span className="text-[#E91E63]">*</span>
                          </label>
                          <div className="w-full">
                            <div className="flex items-center border-b-[2px] border-[#E4E4E7] focus-within:border-[#1A1A1A] transition-colors pb-3">
                              <div className="relative pr-2 border-r border-[#E4E4E7] mr-2 flex items-center h-full">
                                <div className="flex items-center justify-center gap-1 text-[14px] text-[#666] leading-none py-1">
                                  <span className="text-[18px] leading-none mb-[2px]">
                                    🇮🇳
                                  </span>
                                  <span className="leading-none mt-[2px]">
                                    +91
                                  </span>
                                </div>
                              </div>
                              <input
                                inputMode="numeric"
                                placeholder="Enter phone number"
                                aria-label="Phone number"
                                className="flex-1 px-1 py-1 text-[16px] bg-transparent outline-none text-[#1A1A1A] placeholder:text-gray-400"
                                type="tel"
                                required
                                maxLength={10}
                                pattern="[0-9]{10}"
                                value={reviewPhone}
                                onChange={(e) => setReviewPhone(e.target.value)}
                                onInput={(e) => {
                                  e.currentTarget.value = e.currentTarget.value
                                    .replace(/[^0-9]/g, "")
                                    .slice(0, 10);
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <label className="text-[14px] text-[#1A1A1A] font-sans mb-2 block">
                            Rating <span className="text-[#E91E63]">*</span>
                          </label>
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => {
                                const isFull = reviewRating >= star;
                                const isHalf =
                                  reviewRating >= star - 0.5 &&
                                  reviewRating < star;

                                return (
                                  <div
                                    key={star}
                                    className="relative w-6 h-6 flex items-center justify-center"
                                  >
                                    {/* Background empty star */}
                                    <svg
                                      className="w-6 h-6 text-gray-200 fill-current absolute"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>

                                    {/* Foreground animated star */}
                                    <motion.div
                                      initial={false}
                                      animate={{
                                        clipPath: isFull
                                          ? "inset(0 0% 0 0)"
                                          : isHalf
                                            ? "inset(0 50% 0 0)"
                                            : "inset(0 100% 0 0)",
                                      }}
                                      transition={{
                                        duration: 0.3,
                                        ease: "easeInOut",
                                      }}
                                      className="absolute inset-0"
                                    >
                                      <svg
                                        className="w-6 h-6 text-[#DFB37A] fill-current"
                                        viewBox="0 0 20 20"
                                      >
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                      </svg>
                                    </motion.div>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="flex items-center gap-2">
                              <select
                                value={reviewRating}
                                onChange={(e) =>
                                  setReviewRating(Number(e.target.value))
                                }
                                className="border border-[#E4E4E7] rounded-md px-2 py-1 bg-transparent text-[14px] text-[#1A1A1A] font-medium focus:outline-none focus:border-[#DFB37A] transition-colors"
                              >
                                <option value="5">5</option>
                                <option value="4.5">4.5</option>
                                <option value="4">4</option>
                                <option value="3.5">3.5</option>
                                <option value="3">3</option>
                                <option value="2.5">2.5</option>
                                <option value="2">2</option>
                                <option value="1.5">1.5</option>
                                <option value="1">1</option>
                                <option value="0.5">0.5</option>
                              </select>
                              <span className="text-[14px] font-medium text-gray-500">
                                / 5
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                            Review <span className="text-[#E91E63]">*</span>
                          </label>
                          <textarea
                            placeholder="Tell us what you liked about Kayoki fragrance"
                            required
                            value={reviewDesc}
                            onChange={(e) => setReviewDesc(e.target.value)}
                            className="w-full bg-transparent text-[#1A1A1A] placeholder:text-gray-400 text-[16px] font-sans focus:outline-none border-b-[2px] pb-3 border-[#E4E4E7] min-h-[80px]"
                          ></textarea>
                        </div>

                        <div>
                          <label className="text-[14px] text-[#1A1A1A] font-sans mb-2 block">
                            Images (up to 3)
                          </label>
                          <div className="flex items-center gap-4">
                            <label className="cursor-pointer bg-gray-100 text-black py-2 px-4 rounded-full text-[13px] font-semibold hover:bg-gray-200 transition-colors">
                              Choose Files
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                            </label>
                            <span className="text-sm text-gray-500">
                              {reviewImages.length > 0
                                ? `${reviewImages.length} photo${reviewImages.length > 1 ? "s" : ""}`
                                : "No file chosen"}
                            </span>
                          </div>
                          {imageError && (
                            <p className="text-[#E91E63] text-[13px] mt-2 animate-in fade-in">
                              {imageError}
                            </p>
                          )}
                          {reviewImages.length > 0 && (
                            <div className="flex gap-2 mt-3">
                              {reviewImages.map((img, i) => (
                                <div key={i} className="relative w-14 h-14">
                                  <img
                                    src={img || undefined}
                                    alt="Upload preview"
                                    className="w-full h-full object-cover rounded-md"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReviewImages((prev) =>
                                        prev.filter((_, idx) => idx !== i),
                                      );
                                      setImageError("");
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="pt-2">
                          <button
                            type="submit"
                            disabled={
                              isSubmittingReview ||
                              reviewPhone.length < 10 ||
                              reviewName.length < 2 ||
                              reviewDesc.length < 3
                            }
                            className="w-full h-[50px] bg-[#1A1A1A] text-white font-sans font-semibold text-[15px] hover:bg-[#333] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                          >
                            {isSubmittingReview
                              ? "Processing..."
                              : "Submit Review"}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        {/* Mobile Fixed CTA */}
        <div className="md:hidden fixed bottom-0 left-0 w-full px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-[100] box-border overflow-hidden">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col shrink-0">
              <span className="text-[12px] text-gray-500 font-medium">
                Price
              </span>
              <span className="text-[18px] font-bold text-[#1A1A1A] leading-none flex flex-col gap-1">
                {isDiscountActive && product?.discountPrice ? (
                  <>
                    <span className="flex items-baseline gap-1.5">
                      <span className="text-green-600">₹{parseInt(String(product.discountPrice).replace(/[^0-9]/g, ""), 10).toLocaleString('en-IN')}</span>
                      <span className="text-[12px] line-through text-neutral-400 font-normal">{formatPrice(product?.price || 45000)}</span>
                    </span>
                    {timeLeft && (
                      <span className="flex items-center text-[10px] text-red-500 font-medium tracking-wide">
                        Ends in {String(timeLeft.days * 24 + timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
                      </span>
                    )}
                  </>
                ) : (
                  formatPrice(product?.price || 45000)
                )}
              </span>
            </div>
            <button
              onClick={ctaText === "Buy Now" ? onBuyNow : onWaitlist}
              disabled={ctaDisabled}
              className={`flex-1 relative overflow-hidden max-w-[200px] shrink-0 ml-auto p-3.5 rounded-full font-sans font-semibold text-[15px] transition-colors ${ctaDisabled ? "bg-neutral-300 text-neutral-500 cursor-not-allowed" : "bg-black text-white active:scale-95"} ${ctaText === "Buy Now" && !ctaDisabled ? "animate-glass-shine hover:bg-[#222]" : ""}`}
            >
              <span className="relative z-10">{ctaText}</span>
            </button>
          </div>
        </div>
        {/* Description Modal */}
        {activeDescModalContent && (
          <div 
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => {
              trackEvent("close_bundle_item_description", {
                bundle_id: product?.id,
                bundle_name: product?.name,
                item_name: activeDescModalContent.name
              });
              setActiveDescModalContent(null);
            }}
          >
            <div 
              className="bg-white rounded-2xl max-w-lg w-full p-6 relative shadow-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  trackEvent("close_bundle_item_description", {
                    bundle_id: product?.id,
                    bundle_name: product?.name,
                    item_name: activeDescModalContent.name
                  });
                  setActiveDescModalContent(null);
                }}
                aria-label="Close description modal"
                className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors cursor-pointer z-10 bg-white/80 rounded-full p-1"
              >
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-xl font-sans font-semibold mb-4 pr-8 text-[#1A1A1A] shrink-0">
                {activeDescModalContent.name}
              </h3>
              <div className="text-[#444444] font-sans text-[14px] leading-[150%] whitespace-pre-wrap overflow-y-auto min-h-0 pr-2">
                {activeDescModalContent.description}
              </div>
            </div>
          </div>
        )}

        {/* Buying a Kayoki Modal */}
        <AnimatePresence>
          {activeKayokiModal && (
            <div className="fixed inset-0 flex items-center justify-center z-[250]">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/40 backdrop-blur-md cursor-pointer"
                onClick={() => setActiveKayokiModal(null)}
              ></motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="relative bg-white rounded-2xl max-w-[460px] w-full mx-4 overflow-hidden shadow-2xl z-10"
              >
                <div className="flex items-center justify-between p-4 pb-4 px-6">
                  <h2 className="text-[#1A1A1A] text-[24px] font-sans font-semibold capitalize">
                    {activeKayokiModal.title}
                  </h2>
                  <button
                    onClick={() => setActiveKayokiModal(null)}
                    className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center rounded-full bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors cursor-pointer"
                    aria-label="Close modal"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M15 5L5 15M5 5L15 15"
                        stroke="#1A1A1A"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div className="px-0">
                  <div className="overflow-hidden">
                    <img
                      loading="lazy"
                      alt={activeKayokiModal.title}
                      className="w-full object-cover"
                      src={activeKayokiModal.img || undefined}
                      style={{ height: "280px", maxWidth: "none" }}
                    />
                  </div>
                </div>
                <div className="p-6 pt-5">
                  <p className="text-[#1A1A1A] text-[16px] font-sans font-normal leading-[170%] text-justify">
                    {activeKayokiModal.desc}
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
